import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Modal,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Visibility,
  Phone,
  Language,
  RateReview,
  PostAdd,
  PhotoLibrary,
  Build,
  Category as KeywordIcon,
  Download as DownloadIcon,
  DateRange,
  Email as EmailIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import axios from 'axios';
import { useBusiness } from '../contexts/BusinessContext';
import RegionalKeywordsRankingAnalysisTable from '../components/RegionalKeywordsRankingAnalysisTable';
import SplitMapComparison from '../components/ranking/SplitMapComparison';
import { exportToPDF, generateReportFilename } from '../utils/pdfExport';

const COLORS = ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4'];

const ReportPage = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedBusiness } = useBusiness();

  // Initialize date range from URL parameter or default
  const getInitialDateRange = () => {
    const durationParam = searchParams.get('duration');
    if (durationParam) {
      const parsed = parseDurationFromUrl(durationParam);
      if (parsed) return parsed;
    }
    return getPreviousMonthDateRange();
  };

  // Helper function to parse duration from URL parameter (e.g., "1.4.2026 to 1.7.2026")
  const parseDurationFromUrl = (durationParam) => {
    if (!durationParam) return null;

    try {
      // Format: "DD.MM.YYYY to DD.MM.YYYY"
      const parts = durationParam.split('to').map(p => p.trim());
      if (parts.length !== 2) return null;

      const parseGermanDate = (dateStr) => {
        const [day, month, year] = dateStr.split('.').map(Number);
        if (!day || !month || !year) return null;
        // Return ISO format: YYYY-MM-DD
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      };

      const startDate = parseGermanDate(parts[0]);
      const endDate = parseGermanDate(parts[1]);

      if (startDate && endDate) {
        console.log(`📅 Parsed duration from URL: ${startDate} to ${endDate}`);
        return { startDate, endDate };
      }
    } catch (err) {
      console.warn('Failed to parse duration parameter:', err);
    }
    return null;
  };

  // Helper function to get previous month's date range (1st to last day of previous month)
  const getPreviousMonthDateRange = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    // First day of previous month
    const firstDayOfPreviousMonth = new Date(currentYear, currentMonth - 1, 1);

    // Last day of previous month (first day of current month - 1 day)
    const lastDayOfPreviousMonth = new Date(currentYear, currentMonth, 0);

    // Format as YYYY-MM-DD using local date values (avoiding timezone issues)
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatLocalDate(firstDayOfPreviousMonth),
      endDate: formatLocalDate(lastDayOfPreviousMonth)
    };
  };

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(getPreviousMonthDateRange());
  const [duration, setDuration] = useState('');
  const [lastFetchedBusinessId, setLastFetchedBusinessId] = useState(null);

  // Initialize date range from URL parameter if provided
  useEffect(() => {
    const durationParam = searchParams.get('duration');
    if (durationParam) {
      const parsed = parseDurationFromUrl(durationParam);
      if (parsed) {
        console.log(`✅ Applied date range from URL parameter: ${parsed.startDate} to ${parsed.endDate}`);
        setDateRange(parsed);
      }
    }
  }, [searchParams]);

  // PDF Generation state
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [successRecipients, setSuccessRecipients] = useState(''); // Store recipient list for success message

  // Email preview and edit state
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailGenerating, setEmailGenerating] = useState(false);
  const [generatedEmailHtml, setGeneratedEmailHtml] = useState('');
  const [editableEmailContent, setEditableEmailContent] = useState('');

  // Email recipient selection state
  const [recipientDialogOpen, setRecipientDialogOpen] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [customEmail, setCustomEmail] = useState('');
  const [availableRecipients, setAvailableRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  // Fetch available recipients from backend
  const { user } = useAuth();

  useEffect(() => {
    const fetchRecipients = async () => {
      try {
        // Ensure user is authenticated
        const token = localStorage.getItem('auth_token');

        if (!token) {
          setAvailableRecipients([]);
          setRecipientsLoading(false);
          return;
        }

        setRecipientsLoading(true);

        const response = await axios.get('/api/reports/recipients', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success && response.data.recipients && response.data.recipients.length > 0) {
          setAvailableRecipients(response.data.recipients);
          // Don't select any recipients by default - user must manually select
          //setSelectedRecipients([response.data.recipients[0].email]);                                                                                                                         
          setSelectedRecipients([]);
        } else {
          setAvailableRecipients([]);
        }
      } catch (error) {
        console.error('Failed to load report recipients:', error.message);
        console.error('Error status:', error.response?.status);
        console.error('Error response:', error.response?.data);
        setAvailableRecipients([]);
        setSelectedRecipients([]);
      } finally {
        setRecipientsLoading(false);
      }
    };

    // Only fetch when user is authenticated
    if (user) {
      fetchRecipients();
    }
  }, [user]);

  // AI Analysis state
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiError, setAiError] = useState(null);

  const reportRef = useRef(null);
  const emailReportRef = useRef(null); // For email-specific PDF with only Activity Timeline + Filtered Maps

  // Ranking history and comparison state
  const [rankingHistory, setRankingHistory] = useState([]);
  const [allRankingSnapshots, setAllRankingSnapshots] = useState([]); // Keep full list for keyword analysis
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // Activity report state (posts, media, reviews)
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  // All-time insights state
  const [allTimeInsights, setAllTimeInsights] = useState(null);
  const [allTimeInsightsLoading, setAllTimeInsightsLoading] = useState(false);

  // Helper function to get baseline and comparison snapshots for date range
  const getSnapshotsForDateRange = (snapshots, startDate, endDate) => {
    if (snapshots.length === 0) return snapshots;

    // Convert date strings (YYYY-MM-DD) to comparable format
    const getDateKey = (dateStr) => {
      if (typeof dateStr === 'string' && dateStr.includes('-')) {
        return dateStr.split('T')[0]; // Get YYYY-MM-DD part
      }
      const d = new Date(dateStr);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const reportStartKey = getDateKey(startDate);
    const reportEndKey = getDateKey(endDate);

    console.log(`📅 Date range: ${reportStartKey} to ${reportEndKey}`);

    // Sort snapshots by full datetime (date + time)
    const sortedSnapshots = [...snapshots].sort((a, b) => {
      const dateA = new Date(a.analyzedAt || a.snapshotDate);
      const dateB = new Date(b.analyzedAt || b.snapshotDate);
      return dateA.getTime() - dateB.getTime();
    });

    // Find snapshots within the date range
    const snapshotsInRange = sortedSnapshots.filter(s => {
      const snapshotDateKey = getDateKey(s.analyzedAt || s.snapshotDate);
      const isInRange = snapshotDateKey >= reportStartKey && snapshotDateKey <= reportEndKey;
      console.log(`📊 Snapshot ${snapshotDateKey} (${new Date(s.analyzedAt || s.snapshotDate).toLocaleTimeString('de-DE')}): ${isInRange ? '✓ IN RANGE' : '✗ out of range'}`);
      return isInRange;
    });

    // Process snapshots in range: get latest from first date and latest from last date
    if (snapshotsInRange.length > 0) {
      // Get unique dates and their latest snapshots
      const snapshotsByDate = {};

      snapshotsInRange.forEach(snapshot => {
        const dateKey = getDateKey(snapshot.analyzedAt || snapshot.snapshotDate);
        // Keep the latest snapshot for each date (will overwrite older ones)
        snapshotsByDate[dateKey] = snapshot;
      });

      const uniqueDates = Object.keys(snapshotsByDate).sort();
      const baselineSnapshot = snapshotsByDate[uniqueDates[0]];
      const comparisonSnapshot = snapshotsByDate[uniqueDates[uniqueDates.length - 1]];

      console.log(`✅ Found ${snapshotsInRange.length} snapshots in date range across ${uniqueDates.length} unique dates`);
      console.log(`📌 Baseline: ${getDateKey(baselineSnapshot.analyzedAt || baselineSnapshot.snapshotDate)} @ ${new Date(baselineSnapshot.analyzedAt || baselineSnapshot.snapshotDate).toLocaleTimeString('de-DE')}`);
      console.log(`📌 Comparison: ${getDateKey(comparisonSnapshot.analyzedAt || comparisonSnapshot.snapshotDate)} @ ${new Date(comparisonSnapshot.analyzedAt || comparisonSnapshot.snapshotDate).toLocaleTimeString('de-DE')}`);

      return [baselineSnapshot, comparisonSnapshot];
    }

    // Fallback: find closest snapshots before and after range
    console.log(`⚠️ No snapshots in date range, finding closest snapshots...`);
    let closestBefore = null;
    let closestAfter = null;

    for (const snapshot of sortedSnapshots) {
      const snapshotDateKey = getDateKey(snapshot.analyzedAt || snapshot.snapshotDate);

      if (snapshotDateKey < reportStartKey) {
        closestBefore = snapshot;
      } else if (snapshotDateKey > reportEndKey && !closestAfter) {
        closestAfter = snapshot;
      }
    }

    if (closestBefore && closestAfter) {
      console.log(`✅ Using closest snapshots: ${getDateKey(closestBefore.analyzedAt || closestBefore.snapshotDate)} (before) and ${getDateKey(closestAfter.analyzedAt || closestAfter.snapshotDate)} (after)`);
      return [closestBefore, closestAfter];
    }

    // Last resort: return last 2 snapshots
    const lastTwo = sortedSnapshots.slice(-2);
    console.log(`⚠️ Fallback: using last 2 snapshots`);
    return lastTwo;
  };

  // Load ranking history for snapshot comparison
  const loadRankingHistory = async () => {
    if (!locationId) return;

    try {
      setSnapshotLoading(true);
      const response = await axios.get(`/api/businesses/${locationId}/ranking-history`);

      if (response.data.success) {
        let snapshots = response.data.data?.snapshots || [];

        // If no snapshots but keywordHistory exists, transform it
        if (snapshots.length === 0 && response.data.data?.keywordHistory) {
          const keywordHistory = response.data.data.keywordHistory;
          const dateGroups = {};

          Object.keys(keywordHistory).forEach(keyword => {
            keywordHistory[keyword].forEach(entry => {
              const dateKey = entry.date.split('T')[0];

              if (!dateGroups[dateKey]) {
                dateGroups[dateKey] = {
                  snapshotDate: entry.date,
                  analyzedAt: entry.date,
                  keywords: [],
                  isSimulation: false
                };
              }

              const keywordData = {
                keyword: keyword,
                averageRank: entry.averageRank,
                testLocations: entry.locations.map(loc => ({
                  locationName: loc.locationName,
                  lat: loc.coordinates?.lat,
                  lng: loc.coordinates?.lng,
                  rank: loc.rank
                }))
              };

              dateGroups[dateKey].keywords.push(keywordData);
            });
          });

          snapshots = Object.values(dateGroups).sort((a, b) =>
            new Date(a.analyzedAt || a.snapshotDate) - new Date(b.analyzedAt || b.snapshotDate)
          );
        }

        // Keep full list for keyword analysis
        setAllRankingSnapshots(snapshots);

        // Filter snapshots to match report date range (2 snapshots: baseline and comparison)
        const filteredSnapshots = getSnapshotsForDateRange(snapshots, dateRange.startDate, dateRange.endDate);

        console.log(`✅ Loaded ${filteredSnapshots.length} ranking snapshots for report date range`, {
          reportStart: dateRange.startDate,
          reportEnd: dateRange.endDate,
          totalSnapshots: snapshots.length,
          filteredSnapshots: filteredSnapshots.length,
          allSnapshots: snapshots.length
        });
        setRankingHistory(filteredSnapshots);
      }
    } catch (err) {
      console.error('Error loading ranking history:', err);
    } finally {
      setSnapshotLoading(false);
    }
  };

  // Calculate duration
  useEffect(() => {
    const calculateDuration = (start, end) => {
      const startDate = new Date(start);
      const endDate = new Date(end);

      let years = endDate.getFullYear() - startDate.getFullYear();
      let months = endDate.getMonth() - startDate.getMonth();
      let days = endDate.getDate() - startDate.getDate();

      // Adjust for negative days
      if (days < 0) {
        months--;
        // Get the last day of the previous month
        const lastDayOfMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 0).getDate();
        days += lastDayOfMonth;
      }

      // Adjust for negative months
      if (months < 0) {
        years--;
        months += 12;
      }

      // Build the duration string
      const parts = [];
      if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
      if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
      if (days > 0 || parts.length === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);

      return parts.join(' ');
    };

    setDuration(calculateDuration(dateRange.startDate, dateRange.endDate));
  }, [dateRange]);

  const fetchReportData = async () => {
    if (!locationId) {
      setError('No location selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch activity data
      const activityResponse = await axios.post('/api/gmb/activity', {
        locationId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      // Fetch insights data for current period
      const startDateParts = dateRange.startDate.split('-');
      const endDateParts = dateRange.endDate.split('-');

      const insightsResponse = await axios.post(`/api/businesses/${locationId}/insights`, {
        metrics: [
          'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH',
          'BUSINESS_IMPRESSIONS_MOBILE_SEARCH',
          'BUSINESS_IMPRESSIONS_DESKTOP_MAPS',
          'BUSINESS_IMPRESSIONS_MOBILE_MAPS',
          'CALL_CLICKS',
          'WEBSITE_CLICKS'
        ],
        dateRange: {
          startDate: {
            year: parseInt(startDateParts[0]),
            month: parseInt(startDateParts[1]),
            day: parseInt(startDateParts[2])
          },
          endDate: {
            year: parseInt(endDateParts[0]),
            month: parseInt(endDateParts[1]),
            day: parseInt(endDateParts[2])
          }
        },
        includeTimeSeries: true
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      // Fetch insights data for previous period (same length)
      const daysDiff = Math.ceil((new Date(dateRange.endDate) - new Date(dateRange.startDate)) / (1000 * 60 * 60 * 24));
      const prevEndDate = new Date(dateRange.startDate);
      const prevStartDate = new Date(prevEndDate.getTime() - daysDiff * 24 * 60 * 60 * 1000);

      const prevStartParts = prevStartDate.toISOString().split('T')[0].split('-');
      const prevEndParts = prevEndDate.toISOString().split('T')[0].split('-');

      const prevInsightsResponse = await axios.post(`/api/businesses/${locationId}/insights`, {
        metrics: [
          'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH',
          'BUSINESS_IMPRESSIONS_MOBILE_SEARCH',
          'BUSINESS_IMPRESSIONS_DESKTOP_MAPS',
          'BUSINESS_IMPRESSIONS_MOBILE_MAPS',
          'CALL_CLICKS',
          'WEBSITE_CLICKS'
        ],
        dateRange: {
          startDate: {
            year: parseInt(prevStartParts[0]),
            month: parseInt(prevStartParts[1]),
            day: parseInt(prevStartParts[2])
          },
          endDate: {
            year: parseInt(prevEndParts[0]),
            month: parseInt(prevEndParts[1]),
            day: parseInt(prevEndParts[2])
          }
        },
        includeTimeSeries: true
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      // Fetch service activities (we'll filter by date client-side)
      const servicesResponse = await axios.get(`/api/activities/${locationId}`, {
        params: {
          limit: 1000
        }
      });

      // We'll filter the activities by date range client-side
      // Add time to end date to include the entire day
      const currentPeriodStart = new Date(dateRange.startDate + 'T00:00:00');
      const currentPeriodEnd = new Date(dateRange.endDate + 'T23:59:59');
      const prevPeriodStartStr = prevStartDate.toISOString().split('T')[0];
      const prevPeriodEndStr = prevEndDate.toISOString().split('T')[0];
      const prevPeriodStart = new Date(prevPeriodStartStr + 'T00:00:00');
      const prevPeriodEnd = new Date(prevPeriodEndStr + 'T23:59:59');

      const activityResult = activityResponse.data;
      const insightsResult = insightsResponse.data;
      const prevInsightsResult = prevInsightsResponse.data;

      if (activityResult.success && insightsResult.success && prevInsightsResult.success) {
        // Process insights data
        const timeSeries = insightsResult.data.timeSeries || {};
        const summaryStats = insightsResult.data.summaryStats || {};
        const prevSummaryStats = prevInsightsResult.data.summaryStats || {};

        // Prepare chart data
        const dateMap = new Map();
        Object.keys(timeSeries).forEach(metric => {
          const metricData = timeSeries[metric] || [];
          metricData.forEach((dataPoint) => {
            const dateKey = `${dataPoint.date.year}-${String(dataPoint.date.month).padStart(2, '0')}-${String(dataPoint.date.day).padStart(2, '0')}`;

            if (!dateMap.has(dateKey)) {
              dateMap.set(dateKey, {
                date: dateKey,
                impressions: 0,
                calls: 0,
                websiteClicks: 0
              });
            }

            const entry = dateMap.get(dateKey);
            const value = dataPoint.value || 0;

            if (metric.includes('BUSINESS_IMPRESSIONS')) {
              entry.impressions += value;
            } else if (metric === 'CALL_CLICKS') {
              entry.calls += value;
            } else if (metric === 'WEBSITE_CLICKS') {
              entry.websiteClicks += value;
            }
          });
        });

        const chartData = Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));

        // Calculate totals
        const impressionMetrics = [
          'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH',
          'BUSINESS_IMPRESSIONS_MOBILE_SEARCH',
          'BUSINESS_IMPRESSIONS_DESKTOP_MAPS',
          'BUSINESS_IMPRESSIONS_MOBILE_MAPS'
        ];

        // Current period
        const impressions = impressionMetrics.reduce((sum, metric) => {
          return sum + (summaryStats[metric]?.total || 0);
        }, 0);
        const calls = summaryStats.CALL_CLICKS?.total || 0;
        const websiteClicks = summaryStats.WEBSITE_CLICKS?.total || 0;

        // Previous period
        const prevImpressions = impressionMetrics.reduce((sum, metric) => {
          return sum + (prevSummaryStats[metric]?.total || 0);
        }, 0);
        const prevCalls = prevSummaryStats.CALL_CLICKS?.total || 0;
        const prevWebsiteClicks = prevSummaryStats.WEBSITE_CLICKS?.total || 0;

        const stats = {
          impressions: { current: impressions, previous: prevImpressions },
          calls: { current: calls, previous: prevCalls },
          websiteClicks: { current: websiteClicks, previous: prevWebsiteClicks }
        };

        // Count activities - need to fetch previous period posts as well
        const prevActivityResponse = await axios.post('/api/gmb/activity', {
          locationId,
          startDate: prevStartDate.toISOString().split('T')[0],
          endDate: prevEndDate.toISOString().split('T')[0]
        }, {
          headers: { 'Content-Type': 'application/json' }
        });

        const prevActivityResult = prevActivityResponse.data;

        const activities = {
          postsCount: { current: (activityResult.data.posts || []).length, previous: (prevActivityResult.data?.posts || []).length },
          impressionsCount: (activityResult.data.posts || []).filter(p => p.engagement?.views > 0).length,
          reviewsCount: (activityResult.data.reviews || []).length,
          mediasCount: (activityResult.data.medias || []).length,
          keywordsCount: (activityResult.data.keywords || []).length,
          servicesCount: (servicesResponse.data.data || []).length
        };

        // Analyze keyword changes with ranking history
        const keywords = activityResult.data.keywords || [];

        console.log(`Total keywords fetched: ${keywords.length}`);
        if (keywords.length > 0) {
          console.log('Sample keyword data:', keywords[0]);
          console.log('First 5 keywords:', keywords.slice(0, 5).map(k => ({
            keyword: k.keyword,
            averageRank: k.averageRank,
            bestRank: k.bestRank,
            worstRank: k.worstRank,
            analyzedAt: k.analyzedAt,
            snapshotDate: k.snapshotDate,
            snapshotDateStr: k.snapshotDateStr
          })));
        }

        // Group keywords by normalized name (treat 'Badsanierung' and ' badsanierung' as same keyword)
        const keywordMap = new Map();
        const keywordVersions = new Map();
        const keywordDisplayNames = new Map(); // Map normalized to display name

        

        keywords.forEach(kw => {
          // Normalize for grouping: trim and lowercase
          const normalizedKeyword = kw.keyword?.trim().toLowerCase();
          const keywordName = kw.keyword;
          if (normalizedKeyword) {
            // Keep track of all versions of this keyword for comparison
            if (!keywordVersions.has(normalizedKeyword)) {
              keywordVersions.set(normalizedKeyword, []);
            }
            keywordVersions.get(normalizedKeyword).push(kw);

            // Store display name (prefer capitalized version)
            if (!keywordDisplayNames.has(normalizedKeyword)) {
              keywordDisplayNames.set(normalizedKeyword, keywordName);
            } else {
              const existing = keywordDisplayNames.get(normalizedKeyword);
              if (keywordName.trim()[0]?.match(/[A-Z]/) && !existing.trim()[0]?.match(/[A-Z]/)) {
                keywordDisplayNames.set(normalizedKeyword, keywordName);
              }
            }

            // For the main map, keep the one with more recent data
            if (!keywordMap.has(normalizedKeyword)) {
              keywordMap.set(normalizedKeyword, kw);
            } else {
              const existing = keywordMap.get(normalizedKeyword);
              const kwTime = kw.analyzedAt ? new Date(kw.analyzedAt).getTime() : kw.snapshotDate ? new Date(kw.snapshotDate).getTime() : 0;
              const existingTime = existing.analyzedAt ? new Date(existing.analyzedAt).getTime() : existing.snapshotDate ? new Date(existing.snapshotDate).getTime() : 0;
              if (kwTime > existingTime) {
                keywordMap.set(normalizedKeyword, kw);
              }
            }
          }
        });

        // Log deduplication results
        console.log(`Keywords after deduplication: ${keywordMap.size}`);
        const multiVersionKeywords = Array.from(keywordVersions.entries()).filter(([_, versions]) => versions.length > 1);
        console.log(`Keywords with multiple versions: ${multiVersionKeywords.length}`);
        if (multiVersionKeywords.length > 0) {
          console.log('Multi-version keywords:', multiVersionKeywords.map(([name, versions]) => ({
            keyword: name,
            count: versions.length,
            ranks: versions.map(v => v.averageRank),
            dates: versions.map(v => v.analyzedAt || v.snapshotDate)
          })));
        }

        // Add ranking change detection using all available keyword data
        const keywordsWithChanges = Array.from(keywordMap.values()).map(kw => {
          // Use the preferred display name instead of original keyword
          const normalizedKeyword = kw.keyword.trim().toLowerCase();
          const displayName = keywordDisplayNames.get(normalizedKeyword) || kw.keyword;
          let rankingChange = null;
          let changeType = 'nochange'; // 'improved', 'declined', 'nochange', 'added'
          let changeText = 'No change';
          let previousRank = null;
          let currentRank = kw.averageRank;

          // Calculate best and worst ranks across all versions of this keyword (use normalized match)
          const allVersions = keywordVersions.get(kw.keyword.trim().toLowerCase()) || [kw];
          let bestRankOverall = kw.bestRank;
          let worstRankOverall = kw.worstRank;

          if (allVersions.length > 1) {
            const allRanks = allVersions
              .flatMap(v => {
                const ranks = [];
                if (v.bestRank) ranks.push(v.bestRank);
                if (v.averageRank) ranks.push(v.averageRank);
                if (v.worstRank) ranks.push(v.worstRank);
                if (v.rankingHistory) {
                  v.rankingHistory.forEach(h => {
                    if (h.bestRank) ranks.push(h.bestRank);
                    if (h.averageRank) ranks.push(h.averageRank);
                  });
                }
                return ranks;
              })
              .filter(r => r && !isNaN(r));

            if (allRanks.length > 0) {
              bestRankOverall = Math.min(...allRanks);
              worstRankOverall = Math.max(...allRanks);
            }
          }

          console.log(`📊 Analyzing keyword: "${kw.keyword}", data:`, {
            averageRank: kw.averageRank,
            bestRank: kw.bestRank,
            bestRankOverall: bestRankOverall,
            worstRank: kw.worstRank,
            worstRankOverall: worstRankOverall,
            hasRankingHistory: !!kw.rankingHistory,
            rankingHistoryLength: kw.rankingHistory ? kw.rankingHistory.length : 0,
            testLocationsCount: kw.testLocations ? kw.testLocations.length : 0,
            hasMultipleVersions: keywordVersions.get(kw.keyword.trim().toLowerCase())?.length > 1
          });

          // Strategy 0: Use ALL ranking history snapshots to find baseline (NEW)
          // This uses the full unfiltered snapshot list to find earliest snapshot with keyword
          if (!previousRank && allRankingSnapshots && allRankingSnapshots.length >= 2) {
            const normalizedKwName = normalizedKeyword;
            const dateRangeStart = new Date(dateRange.startDate);
            const dateRangeEnd = new Date(dateRange.endDate);

            // Find earliest snapshot in date range that has this keyword
            let baselineKeyword = null;
            let comparisonKeyword = null;

            for (const snapshot of allRankingSnapshots) {
              const snapshotDate = new Date(snapshot.analyzedAt || snapshot.snapshotDate);
              const isInRange = snapshotDate >= dateRangeStart && snapshotDate <= dateRangeEnd;

              if (!isInRange) continue;

              if (snapshot?.keywords) {
                const found = snapshot.keywords.find(k => k.keyword?.trim().toLowerCase() === normalizedKwName);
                if (found && !baselineKeyword) {
                  // Use first match as baseline
                  baselineKeyword = found;
                  console.log(`  Strategy 0: Found baseline keyword in snapshot ${snapshot.analyzedAt || snapshot.snapshotDate}`);
                }
                if (found) {
                  // Keep updating to get the latest
                  comparisonKeyword = found;
                }
              }
            }

            console.log(`  Strategy 0 (All Snapshots): baseline=${baselineKeyword?.averageRank}, comparison=${comparisonKeyword?.averageRank}`);

            if (baselineKeyword?.averageRank && comparisonKeyword?.averageRank) {
              previousRank = baselineKeyword.averageRank;
              currentRank = comparisonKeyword.averageRank;
              console.log(`  Using Strategy 0: ${previousRank} -> ${currentRank}`);
            }
          }

          // Strategy 1: Use rankingHistory if available (with detailed logging)
          if (!previousRank && kw.rankingHistory && kw.rankingHistory.length >= 2) {
            const latest = kw.rankingHistory[kw.rankingHistory.length - 1];
            const previous = kw.rankingHistory[kw.rankingHistory.length - 2];

            console.log(`  Strategy 1 (History): latest.averageRank=${latest?.averageRank}, previous.averageRank=${previous?.averageRank}`);

            if (latest?.averageRank && previous?.averageRank) {
              previousRank = previous.averageRank;
              currentRank = latest.averageRank;
              console.log(`  Using Strategy 1: ${previousRank} -> ${currentRank}`);
            }
          }

          // Strategy 2: Compare first vs last version from date range
          if (!previousRank) {
            const versions = keywordVersions.get(kw.keyword.trim().toLowerCase());
            if (versions && versions.length >= 2) {
              // Sort by date
              const sorted = [...versions].sort((a, b) => {
                const aTime = a.analyzedAt ? new Date(a.analyzedAt).getTime() : 0;
                const bTime = b.analyzedAt ? new Date(b.analyzedAt).getTime() : 0;
                return aTime - bTime;
              });

              const firstVersion = sorted[0];
              const lastVersion = sorted[sorted.length - 1];

              console.log(`  Strategy 2 (First vs Last): first.averageRank=${firstVersion?.averageRank}, last.averageRank=${lastVersion?.averageRank}`);

              if (firstVersion?.averageRank && lastVersion?.averageRank) {
                previousRank = firstVersion.averageRank;
                currentRank = lastVersion.averageRank;
                console.log(`  Using Strategy 2: ${previousRank} -> ${currentRank}`);
              }
            }
          }

          // Strategy 3: Use testLocations ranks if available
          if (!previousRank && kw.testLocations && kw.testLocations.length > 0) {
            const ranks = kw.testLocations
              .filter(loc => loc.rank && !isNaN(loc.rank))
              .map(loc => loc.rank);

            console.log(`  Strategy 3 (TestLocations): found ${ranks.length} ranks:`, ranks);

            if (ranks.length > 1) {
              const minRank = Math.min(...ranks);
              const maxRank = Math.max(...ranks);
              previousRank = maxRank;
              currentRank = minRank;
              console.log(`  Using Strategy 3: ${previousRank} -> ${currentRank}`);
            }
          }

          // Strategy 4: Use bestRank vs worstRank
          if (!previousRank && kw.bestRank && kw.worstRank) {
            console.log(`  Strategy 4 (Best/Worst): bestRank=${kw.bestRank}, worstRank=${kw.worstRank}`);

            if (kw.bestRank !== kw.worstRank) {
              previousRank = kw.worstRank;
              currentRank = kw.bestRank;
              console.log(`  Using Strategy 4: ${previousRank} -> ${currentRank}`);
            }
          }

          // Determine change type
          if (previousRank && currentRank) {
            rankingChange = {
              from: previousRank,
              to: currentRank
            };

            if (currentRank < previousRank) {
              changeType = 'improved';
              changeText = `${previousRank} -> ${currentRank}`;
              console.log(`  Result: IMPROVED - ${changeText}`);
            } else if (currentRank > previousRank) {
              changeType = 'declined';
              changeText = `${previousRank} -> ${currentRank}`;
              console.log(`  Result: DECLINED - ${changeText}`);
            } else {
              changeText = `${currentRank}`;
              console.log(`  Result: NO CHANGE - ${changeText}`);
            }
          } else if (kw.addedDate) {
            changeType = 'added';
            changeText = 'New';
            rankingChange = { from: null, to: kw.averageRank };
            console.log(`  Result: NEW - ${changeText}`);
          } else if (currentRank) {
            changeText = `${currentRank}`;
            console.log(`  Result: CURRENT ONLY - ${changeText}`);
          }

          return {
            ...kw,
            keyword: displayName,
            rankingChange,
            changeType,
            changeText,
            bestRank: bestRankOverall,
            worstRank: worstRankOverall
          };
        });

        // Count ranking changes
        let keywordMetrics = {
          improved: 0,
          declined: 0,
          added: 0,
          nochange: 0,
          totalKeywords: keywordsWithChanges.length,
          topRanked: 0,        // Keywords ranking in top 10
          midRanked: 0,        // Keywords ranking 11-30
          lowRanked: 0         // Keywords ranking 31+
        };

        keywordsWithChanges.forEach(kw => {
          // Count ranking changes
          if (kw.changeType === 'improved') {
            keywordMetrics.improved++;
          } else if (kw.changeType === 'declined') {
            keywordMetrics.declined++;
          } else if (kw.changeType === 'added') {
            keywordMetrics.added++;
          } else {
            keywordMetrics.nochange++;
          }

          // Categorize by ranking tier
          if (kw.averageRank) {
            const avgRank = kw.averageRank;
            if (avgRank <= 10) {
              keywordMetrics.topRanked++;
            } else if (avgRank <= 30) {
              keywordMetrics.midRanked++;
            } else {
              keywordMetrics.lowRanked++;
            }
          }
        });

        // Service activities breakdown - filter by date range
        const allServiceActivities = servicesResponse.data.data || [];

        // Helper function to filter activities by date range
        const filterActivitiesByDate = (activities, startDate, endDate) => {
          return activities.filter(activity => {
            if (!activity.timestamp) return false;
            const activityDate = new Date(activity.timestamp);
            return activityDate >= startDate && activityDate <= endDate;
          });
        };

        const currentPeriodActivities = filterActivitiesByDate(allServiceActivities, currentPeriodStart, currentPeriodEnd);
        const prevPeriodActivities = filterActivitiesByDate(allServiceActivities, prevPeriodStart, prevPeriodEnd);

        console.log('📊 Service Activities Report:');
        console.log(`  Total activities fetched: ${allServiceActivities.length}`);
        console.log(`  Date range filtering:`);
        console.log(`    Current period: ${currentPeriodStart.toISOString()} to ${currentPeriodEnd.toISOString()}`);
        console.log(`    Previous period: ${prevPeriodStart.toISOString()} to ${prevPeriodEnd.toISOString()}`);
        if (allServiceActivities.length > 0) {
          console.log('  Sample activity:', allServiceActivities[0]);
          console.log('  All activity types:', allServiceActivities.map(a => a.activityType));
          console.log('  Sample timestamps:', allServiceActivities.slice(0, 3).map(a => ({ type: a.activityType, timestamp: a.timestamp })));
        }
        console.log(`  Current period (${dateRange.startDate} to ${dateRange.endDate}): ${currentPeriodActivities.length} activities`);
        console.log(`  Previous period: ${prevPeriodActivities.length} activities`);
        console.log('  Current period breakdown:', {
          added: currentPeriodActivities.filter(a => a.activityType === 'service_added').length,
          deleted: currentPeriodActivities.filter(a => a.activityType === 'service_deleted').length,
          descriptionAdded: currentPeriodActivities.filter(a => a.activityType === 'service_description_added').length,
          descriptionUpdated: currentPeriodActivities.filter(a => a.activityType === 'service_description_updated').length
        });
        console.log('  Current period description activities:', currentPeriodActivities.filter(a => a.activityType && a.activityType.includes('description')));

        const serviceMetrics = {
          added: {
            current: currentPeriodActivities.filter(a => a.activityType === 'service_added').length,
            previous: prevPeriodActivities.filter(a => a.activityType === 'service_added').length
          },
          deleted: {
            current: currentPeriodActivities.filter(a => a.activityType === 'service_deleted').length,
            previous: prevPeriodActivities.filter(a => a.activityType === 'service_deleted').length
          },
          descriptionAdded: {
            current: currentPeriodActivities.filter(a => a.activityType === 'service_description_added').length,
            previous: prevPeriodActivities.filter(a => a.activityType === 'service_description_added').length
          },
          descriptionUpdated: {
            current: currentPeriodActivities.filter(a => a.activityType === 'service_description_updated').length,
            previous: prevPeriodActivities.filter(a => a.activityType === 'service_description_updated').length
          }
        };

        console.log('  Service Metrics Summary:');
        console.log('    Added:', serviceMetrics.added);
        console.log('    Deleted:', serviceMetrics.deleted);
        console.log('    Description Added:', serviceMetrics.descriptionAdded);
        console.log('    Description Updated:', serviceMetrics.descriptionUpdated);
        console.log('  Show/Hide Logic:');
        console.log('    Added show?', serviceMetrics.added.current > 0 || serviceMetrics.added.previous > 0);
        console.log('    Deleted show?', serviceMetrics.deleted.current > 0 || serviceMetrics.deleted.previous > 0);
        console.log('    Description Added show?', serviceMetrics.descriptionAdded.current > 0 || serviceMetrics.descriptionAdded.previous > 0);
        console.log('    Description Updated show?', serviceMetrics.descriptionUpdated.current > 0 || serviceMetrics.descriptionUpdated.previous > 0);

        // Fetch regional keywords analysis data
        let regionalAnalysisData = null;
        try {
          const regionalResponse = await axios.post(
            `/api/businesses/${locationId}/regional-keywords-analysis`,
            {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate
            },
            {
              headers: { 'Content-Type': 'application/json' }
            }
          );

          if (regionalResponse.data.success && regionalResponse.data.data.keywords) {
            regionalAnalysisData = regionalResponse.data.data;
            console.log('📊 Regional keywords analysis:', regionalAnalysisData);
          }
        } catch (regionalErr) {
          console.warn('⚠️ Could not fetch regional keywords analysis:', regionalErr.message);
        }

        // Fetch activity data (posts, media, reviews line chart)
        let activityReportData = null;
        try {
          setActivityLoading(true);
          console.log(`📊 Fetching activity for period: ${dateRange.startDate} to ${dateRange.endDate}`);
          const activityResponse = await axios.post(
            '/api/report-activity',
            {
              locationId,
              startDate: dateRange.startDate,
              endDate: dateRange.endDate
            },
            { headers: { 'Content-Type': 'application/json' } }
          );

          if (activityResponse.data.success) {
            activityReportData = activityResponse.data.data;
            setActivityData(activityReportData);
            console.log('📊 Activity report data fetched:');
            console.log('  Chart data points:', activityReportData.chartData?.length || 0);
            console.log('  Period Posts:', activityReportData.summary?.postsCreatedThisPeriod || 0);
            console.log('  Period Media:', activityReportData.summary?.mediasAddedThisPeriod || 0);
            console.log('  All-time Posts:', activityReportData.summary?.postsCreatedAllTime || 0);
            console.log('  All-time Media:', activityReportData.summary?.mediasAddedAllTime || 0);
            console.log('  Business Created:', activityReportData.summary?.dateRange?.businessCreatedAt);
            console.log('  Selected Period:', activityReportData.summary?.dateRange?.reportStart, 'to', activityReportData.summary?.dateRange?.reportEnd);
            console.log('  Full data:', activityReportData);
          }
        } catch (activityErr) {
          console.warn('⚠️ Could not fetch activity report:', activityErr.message);
        } finally {
          setActivityLoading(false);
        }

        // Fetch all-time insights data (from business creation to today)
        let allTimeInsightsData = null;
        try {
          setAllTimeInsightsLoading(true);
          if (activityReportData?.summary?.dateRange?.businessCreatedAt) {
            const businessCreatedDate = new Date(activityReportData.summary.dateRange.businessCreatedAt);
            const businessCreatedParts = activityReportData.summary.dateRange.businessCreatedAt.split('-');
            const todayDate = new Date();
            const todayParts = [todayDate.getFullYear(), String(todayDate.getMonth() + 1).padStart(2, '0'), String(todayDate.getDate()).padStart(2, '0')];

            console.log(`📊 Fetching all-time insights from ${activityReportData.summary.dateRange.businessCreatedAt} to today`);

            const allTimeInsightsResponse = await axios.post(`/api/businesses/${locationId}/insights`, {
              metrics: [
                'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH',
                'BUSINESS_IMPRESSIONS_MOBILE_SEARCH',
                'BUSINESS_IMPRESSIONS_DESKTOP_MAPS',
                'BUSINESS_IMPRESSIONS_MOBILE_MAPS',
                'CALL_CLICKS',
                'WEBSITE_CLICKS'
              ],
              dateRange: {
                startDate: {
                  year: parseInt(businessCreatedParts[0]),
                  month: parseInt(businessCreatedParts[1]),
                  day: parseInt(businessCreatedParts[2])
                },
                endDate: {
                  year: parseInt(todayParts[0]),
                  month: parseInt(todayParts[1]),
                  day: parseInt(todayParts[2])
                }
              }
            }, {
              headers: { 'Content-Type': 'application/json' }
            });

            if (allTimeInsightsResponse.data.success) {
              allTimeInsightsData = allTimeInsightsResponse.data.data;
              setAllTimeInsights(allTimeInsightsData);
              console.log('📊 All-time insights fetched:', allTimeInsightsData);
            }
          }
        } catch (allTimeErr) {
          console.warn('⚠️ Could not fetch all-time insights:', allTimeErr.message);
        } finally {
          setAllTimeInsightsLoading(false);
        }

        // Pre-calculate cumulative impressions chart data for PDF export
        let cumulativeImpressionsChartData = [];
        if (allTimeInsightsData && allTimeInsightsData.timeSeries) {
          const timeSeries = allTimeInsightsData.timeSeries || {};
          const monthlyData = new Map();
          const impressionMetrics = [
            'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH',
            'BUSINESS_IMPRESSIONS_MOBILE_SEARCH',
            'BUSINESS_IMPRESSIONS_DESKTOP_MAPS',
            'BUSINESS_IMPRESSIONS_MOBILE_MAPS'
          ];

          Object.keys(timeSeries).forEach(metric => {
            const metricData = timeSeries[metric] || [];
            metricData.forEach((dataPoint) => {
              const monthKey = `${dataPoint.date.year}-${String(dataPoint.date.month).padStart(2, '0')}`;


             const displayKey = `${String(dataPoint.date.month).padStart(2, '0')}/${String(dataPoint.date.year).slice(-2)}`;                                                                        

              if (!monthlyData.has(monthKey)) {
                monthlyData.set(monthKey, {
                  month: monthKey,
                  displayMonth: displayKey,
                  monthlyImpressions: 0
                });
              }

              const entry = monthlyData.get(monthKey);
              const value = dataPoint.value || 0;

              if (impressionMetrics.includes(metric)) {
                entry.monthlyImpressions += value;
              }
            });
          });

          const sortedData = Array.from(monthlyData.values())
            .sort((a, b) => a.month.localeCompare(b.month))
            .map(item => ({
              date: item.displayMonth,
              impressions: item.monthlyImpressions
            }));

          let cumulativeSum = 0;
          cumulativeImpressionsChartData = sortedData.map(item => {
            cumulativeSum += item.impressions;
            return {
              date: item.date,
              'Cumulative Einblendungen': cumulativeSum
            };
          });
        }

        setReportData({
          stats,
          activities,
          keywordMetrics,
          serviceMetrics,
          chartData,
          allPosts: activityResult.data.posts || [],
          allReviews: activityResult.data.reviews || [],
          allKeywords: keywordsWithChanges,
          allServices: servicesResponse.data.data || [],
          businessInfo: insightsResult.data.businessInfo,
          regionalAnalysis: regionalAnalysisData,
          activityReport: activityReportData,
          allTimeInsights: allTimeInsightsData,
          cumulativeImpressionsChartData
        });

        // Track which business this data is for
        const currentBusinessId = selectedBusiness?.id || selectedBusiness?.googleLocationId || locationId;
        setLastFetchedBusinessId(currentBusinessId);
        console.log('✅ Report data fetched successfully for business:', currentBusinessId);
      } else {
        throw new Error('Failed to fetch report data');
      }
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError(err.response?.data?.error || 'Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when location changes (from URL)
  useEffect(() => {
    if (locationId) {
      console.log('📊 Fetching report for locationId:', locationId);
      setReportData(null); // Clear previous data
      setAiAnalysis(null); // Clear previous AI analysis
      fetchReportData();
      loadRankingHistory(); // Load snapshots for comparison
    }
  }, [locationId]);

  // Trigger AI analysis after report data is ready
  useEffect(() => {
    if (reportData && selectedBusiness && !aiAnalyzing && !aiAnalysis) {
      console.log('🤖 Report data is ready, triggering AI analysis...');
     //handleAIAnalysis();
    }
  }, [reportData, selectedBusiness, aiAnalyzing, aiAnalysis]);

  // Reload ranking history when date range changes
  useEffect(() => {
    if (locationId && rankingHistory.length === 0 && !snapshotLoading) {
      // Only reload if we haven't loaded history yet
      return;
    }
    if (locationId) {
      loadRankingHistory(); // Reload with new date range
    }
  }, [dateRange.startDate, dateRange.endDate]);

  // Handle business selection change - navigate to new business report
  // NOTE: This only navigates if selectedBusiness was explicitly changed (not from auto-select)
  // We detect this by checking if the change came from outside the current URL context
  useEffect(() => {
    if (selectedBusiness && locationId) {
      const businessLocationId = selectedBusiness.googleLocationId || selectedBusiness.locationId || selectedBusiness.id;
      const currentLocationId = locationId?.toString();
      const newLocationId = businessLocationId?.toString();

      console.log('🔄 Business context changed:', {
        businessName: selectedBusiness.businessName,
        newLocationId,
        currentLocationId,
        match: newLocationId === currentLocationId
      });

      // Only navigate if selected business's location doesn't match current URL
      // AND only if this change wasn't triggered by a manual URL edit
      // The selectBusiness function in context already handles navigation when user selects via dropdown
      // This is a safety check to keep them in sync, but we avoid navigating during URL changes
      // by only navigating when needed to maintain consistency
      if (newLocationId && newLocationId !== currentLocationId) {
        console.log('🔀 Keeping business selection in sync with report:', selectedBusiness.businessName, 'ID:', newLocationId);
        // Don't navigate here - let the URL-based auto-select in BusinessSearchContext handle it
        // navigate(`/report/${newLocationId}`, { replace: false });
      }
    }
  }, [selectedBusiness?.id, selectedBusiness?.googleLocationId, selectedBusiness?.locationId, selectedBusiness?.businessName]);

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDownloadPDF = async () => {
    if (!reportData || !emailReportRef.current) {
      setPdfError('No report data available to export');
      return;
    }

    if (!rankingHistory || rankingHistory.length === 0) {
      setPdfError('Ranking data is not available. Please ensure snapshots are loaded.');
      return;
    }

    setPdfGenerating(true);
    setPdfError(null);

    try {
      // Check if html2pdf library is loaded
      if (!window.html2pdf) {
        throw new Error('PDF library is not loaded. Please refresh the page and try again.');
      }

      const businessName = selectedBusiness?.businessName || 'Business';
      const filename = generateReportFilename(
        businessName,
        dateRange.startDate,
        dateRange.endDate
      );

      console.log('📥 Starting PDF download (Email version - Activity Timeline + Filtered Maps):', filename);

      // Use email report content for download (same as email version)
      const reportElement = emailReportRef.current;
      const originalPadding = reportElement.style.paddingTop;
      reportElement.style.paddingTop = '20px';

      try {
        await exportToPDF(reportElement, filename, {
          scale: 2, // High quality
          orientation: 'portrait',
          pageFormat: 'a4',
          margins: { top: 10, bottom: 10, left: 1, right: 1 },
          // Header info for PDF
          headerInfo: {
            businessName: businessName,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
          }
        });

        console.log('✅ PDF downloaded successfully:', filename);
        setPdfSuccess(true);

        // Hide success message after 4 seconds
        setTimeout(() => setPdfSuccess(false), 4000);
      } finally {
        // Restore original padding
        reportElement.style.paddingTop = originalPadding;
      }
    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      setPdfError('Failed to generate PDF. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };



  // Generate email content using AI
  const handleGenerateEmail = async () => {
    if (!reportData || !selectedBusiness) {
      setEmailError('Report data is not ready. Please generate the report first.');
      return;
    }

    setEmailGenerating(true);
    setEmailError(null);

    try {
      const businessName = selectedBusiness?.businessName || 'Business';

      console.log('🤖 Requesting AI email generation...');
      const response = await axios.post('/api/reports/generate-email', {
        businessName,
        locationId: selectedBusiness?.googleLocationId || selectedBusiness?.locationId || locationId, // ✅ FIX: Pass locationId for cumulative data calculation
        reportData: {
          stats: reportData.stats || {},
          activities: reportData.activities || {},
          keywordMetrics: reportData.keywordMetrics || {},
          regionalAnalysis: reportData.regionalAnalysis || {},
          serviceMetrics: reportData.serviceMetrics || {}
        },
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        periodKpiData:{
          impressions:reportData.stats?.impressions?.current,
          calls:reportData.stats?.calls?.current,
          websiteClicks:reportData.stats?.websiteClicks?.current
        }
      });

      if (response.data.success) {
        console.log('✅ Email generated successfully');
        setGeneratedEmailHtml(response.data.data.emailHtml);
        setEditableEmailContent(response.data.data.emailHtml);
        // Open recipient selection dialog first
        setRecipientDialogOpen(true);
      } else {
        throw new Error(response.data.error || 'Failed to generate email');
      }
    } catch (error) {
      console.error('❌ Error generating email:', error);
      setEmailError(`Failed to generate email: ${error.message}`);
    } finally {
      setEmailGenerating(false);
    }
  };

  // Handler for confirming recipient selection and opening email preview
  const handleConfirmRecipients = () => {
    if (selectedRecipients.length === 0 && !customEmail) {
      setEmailError('Please select at least one recipient');
      return;
    }
    setRecipientDialogOpen(false);
    setEmailPreviewOpen(true);
  };

  // Handle recipient checkbox change
  const handleRecipientChange = (email) => {
    setSelectedRecipients(prev =>
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  // Handle sending PDF via email with approved content
  const handleSendEmailWithPDF = async () => {
    if (!reportData || !selectedBusiness) {
      setEmailError('Report data is not ready. Please generate the report first.');
      return;
    }

    if (!reportData.activityReport) {
      setEmailError('Activity report data is not available. Please click "Generate Report" again.');
      return;
    }

    if (!rankingHistory || rankingHistory.length === 0) {
      setEmailError('Ranking data is not available. Please ensure snapshots are loaded.');
      return;
    }

    setEmailSending(true);
    setEmailError(null);

    try {
      const businessName = selectedBusiness?.businessName || 'Business';

      const filename = generateReportFilename(
        businessName,
        dateRange.startDate,
        dateRange.endDate
      );

      console.log('📧 Starting PDF generation and email sending:', filename);
      console.log('📊 Activity Report:', reportData.activityReport);
      console.log('🗺️ Ranking History:', rankingHistory);

      // Use email-specific report content (only Activity Timeline + Filtered Maps)
      const reportElement = emailReportRef.current;
      if (!reportElement) {
        throw new Error('Email report content container not found in DOM');
      }

      console.log('✅ Email report element found:', reportElement);

      const originalPadding = reportElement.style.paddingTop;
      reportElement.style.paddingTop = '20px';

      try {
        // Generate PDF as blob with only Activity Timeline and Filtered Maps
        console.log('📄 Generating PDF blob for email (lower quality for Vercel 5MB limit)...');

        // Use lower scale (1.2 instead of 2) to reduce PDF size for email
        // Fits under Vercel 5MB limit: 4.29MB → ~2.5MB (42% reduction)
        const pdfResult = await exportToPDF(reportElement, filename, {
          scale: 1.2, // Lower quality to reduce file size (~40-50% smaller)
          orientation: 'portrait',
          pageFormat: 'a4',
          returnBlob: true, // Get blob instead of downloading
          // Wider margins for email (narrower left/right for broader content)
          margins: { top: 10, bottom: 10, left: 1, right: 1 },
          // Header info for PDF (same as download)
          headerInfo: {
            businessName: businessName,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
          }
        });

        let finalPdfBlob = pdfResult.blob;

        if (!finalPdfBlob) {
          throw new Error('Failed to generate PDF blob');
        }

        console.log('✅ PDF blob created (compressed):', {
          size: finalPdfBlob.size,
          type: finalPdfBlob.type,
          sizeInMB: (finalPdfBlob.size / (1024 * 1024)).toFixed(2) + ' MB',
          pages: pdfResult.pages,
          note: 'Using scale 1.2 instead of 2 for smaller file size'
        });

        // Validate blob size
        if (finalPdfBlob.size === 0) {
          throw new Error('PDF blob is empty - PDF generation may have failed');
        }
        if (finalPdfBlob.size > 100 * 1024 * 1024) {
          throw new Error('PDF exceeds 100MB limit');
        }

        console.log('📧 Sending email with compressed PDF...');

        // Create FormData for efficient binary upload
        const formData = new FormData();
        formData.append('pdfFile', finalPdfBlob, filename);

        // Add all selected recipients
        selectedRecipients.forEach((email, index) => {
          formData.append(`recipients[${index}]`, email);
        });

        // Add custom email if provided
        if (customEmail && customEmail.trim()) {
          formData.append(`recipients[${selectedRecipients.length}]`, customEmail.trim());
        }

        formData.append('filename', filename);
        formData.append('businessName', businessName);
        formData.append('startDate', dateRange.startDate);
        formData.append('endDate', dateRange.endDate);

        // Add pre-approved/edited email body
        if (editableEmailContent) {
          formData.append('emailBody', editableEmailContent);
        }

        // Send single request with compressed PDF
        const response = await axios.post('/api/reports/send-email', formData, {
          timeout: 120000,
          maxContentLength: 100 * 1024 * 1024,
          maxBodyLength: 100 * 1024 * 1024
        });

        if (response.data.success) {
          // Build complete recipient list including custom email
          const allRecipients = selectedRecipients.concat(customEmail && customEmail.trim() ? [customEmail.trim()] : []);
          const recipientList = allRecipients.join(', ');

          console.log('✅ Report sent successfully to:', {
            recipientList,
            selectedRecipients,
            customEmail,
            allRecipients
          });

          // Store recipient list BEFORE clearing state (used in success alert)
          setSuccessRecipients(recipientList);

          // Close modals immediately to show email was sent
          setEmailPreviewOpen(false);
          setRecipientDialogOpen(false);

          // Clear form fields
          setEditableEmailContent('');
          setCustomEmail('');

          // Show success message for 5 seconds
          setEmailSuccess(true);
          setTimeout(() => {
            setEmailSuccess(false);
            setSuccessRecipients(''); // Clear after alert disappears
          }, 5000);
        } else {
          throw new Error(response.data.message || 'Failed to send email');
        }
      } finally {
        reportElement.style.paddingTop = originalPadding;
      }
    } catch (error) {
      console.error('❌ Error sending email with PDF:', error);

      // Note: Modal stays open on error so user can see the error message and retry
      // Extract error message from response if available
      let errorMessage = 'Failed to send report via email. ';

      // Check if it's a network/API error
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;

        // Add diagnostic info from backend
        if (error.response.data.received) {
          errorMessage += `\n\nReceived by server:\n`;
          errorMessage += `  PDF: ${error.response.data.received.hasPDF ? '✅ Yes' : '❌ No'}\n`;
          errorMessage += `  Email: ${error.response.data.received.hasEmail ? '✅ Yes' : '❌ No'}\n`;
          errorMessage += `  Filename: ${error.response.data.received.hasFilename ? '✅ Yes' : '❌ No'}`;
        }

        // Add troubleshooting info if available
        if (error.response.data.troubleshoot) {
          const tips = error.response.data.troubleshoot;
          errorMessage += '\n\nTroubleshooting:\n';
          Object.values(tips).forEach(tip => {
            errorMessage += `• ${tip}\n`;
          });
        }
      } else if (error.message) {
        // Client-side error (e.g., PDF generation failed)
        errorMessage += error.message;

        if (error.message.includes('Email report element')) {
          errorMessage += '\n\n⚠️ Email report content not found. Try refreshing the page and generating the report again.';
        }
      } else {
        errorMessage += 'Unknown error occurred. Please check the browser console for details.';
      }

      setEmailError(errorMessage);
    } finally {
      setEmailSending(false);
    }
  };

    function formatToGermanDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
}




  // Handle AI Analysis of Report
  const handleAIAnalysis = async () => {
    if (!reportData || !selectedBusiness) {
      setAiError('Report data is not ready. Please generate the report first.');
      return;
    }

    setAiAnalyzing(true);
    setAiError(null);
    setAiAnalysis(null);

    try {
      console.log('🤖 Starting AI analysis of report...');

      // Prepare data for AI analysis (minimal payload to avoid 413 error)
      const analysisData = {
        businessName: selectedBusiness?.businessName,
        dateRange: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        },
        stats: reportData.stats,
        // Send only top 10 keywords with minimal data
        keywordMetrics: reportData.allKeywords?.slice(0, 10).map(kw => ({
          keyword: kw.keyword,
          averageRank: kw.averageRank,
          testLocationsCount: kw.testLocations?.length || 0
        })),
        gmbPerformance: {
          postCount: reportData.allPosts?.length,
          reviewCount: reportData.allReviews?.length,
          averageRating: reportData.stats?.averageRating,
          totalReviews: reportData.stats?.totalReviews
        }
      };

      const response = await axios.post('/api/reports/analyze', analysisData);

      if (response.data.success) {
        console.log('✅ AI analysis completed');
        setAiAnalysis(response.data.analysis);
      } else {
        throw new Error(response.data.message || 'Failed to get AI analysis');
      }
    } catch (error) {
      console.error('❌ Error getting AI analysis:', error);
      setAiError(error.response?.data?.message || error.message || 'Failed to analyze report. Please try again.');
    } finally {
      setAiAnalyzing(false);
    }
  };

  const MetricCard = ({ title, value, previousValue, icon: Icon, color = 'primary' }) => {
    const currentVal = typeof value === 'object' ? value.current : value;
    const prevVal = typeof value === 'object' ? value.previous : previousValue;
    const hasChange = prevVal !== undefined && prevVal !== null;
    const difference = hasChange ? currentVal - prevVal : null;
    const differenceColor = difference > 0 ? '#4caf50' : difference < 0 ? '#f44336' : '#1976d2';

    return (
      <Card sx={{ height: '100%', position: 'relative' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box sx={{ flex: 1 }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                {title}
              </Typography>
              <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: `${color}.main` }}>
                {typeof currentVal === 'number' ? currentVal.toLocaleString() : currentVal}
              </Typography>
              {hasChange && (
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: differenceColor
                    }}
                  >
                    {prevVal.toLocaleString()} → {currentVal.toLocaleString()}
                  </Typography>
                  <Chip
                    label={`${difference > 0 ? '+' : ''}${difference}`}
                    size="small"
                    sx={{
                      fontWeight: 'bold',
                      color: '#fff',
                      backgroundColor: differenceColor
                    }}
                  />
                </Box>
              )}
            </Box>
            <Box sx={{
              bgcolor: `${color}.light`,
              p: 1.5,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon sx={{ color: `${color}.main`, fontSize: 24 }} />
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (!locationId) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">No business location selected</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Progress Report
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body1" color="textSecondary">
            {selectedBusiness?.businessName || 'Business'}
          </Typography>
          {loading && <CircularProgress size={20} />}
          <Chip
            label={`ID: ${selectedBusiness?.id || selectedBusiness?.googleLocationId || locationId}`}
            size="small"
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Date Range Selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <TextField
            label="From"
            type="date"
            value={dateRange.startDate}
            onChange={(e) => handleDateChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={loading}
            size="small"
          />
          <TextField
            label="To"
            type="date"
            value={dateRange.endDate}
            onChange={(e) => handleDateChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={loading}
            size="small"
          />
          <Button
            variant="contained"
            onClick={() => {fetchReportData();
                // handleAIAnalysis();
    
  }}
            disabled={loading}
            startIcon={<DateRange />}
          >
            Generate Report
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadPDF}
            disabled={loading || pdfGenerating || !reportData}
            startIcon={pdfGenerating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            color="success"
            sx={{ ml: 1 }}
          >
            {pdfGenerating ? 'Generating PDF...' : 'Download Report'}
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateEmail}
            disabled={loading || emailGenerating || emailSending || !reportData}
            startIcon={emailGenerating ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
            color="info"
            sx={{ ml: 1 }}
          >
            {emailGenerating ? 'Generating Email...' : 'Send via Email'}
          </Button> 
          {/*
          <Button
            variant="contained"
            onClick={handleAIAnalysis}
            disabled={loading || aiAnalyzing || !reportData}
            startIcon={aiAnalyzing ? <CircularProgress size={20} color="inherit" /> : ''}
            color="warning"
            sx={{ ml: 1 }}
          >
            {aiAnalyzing ? 'Analyzing...' : 'AI Analysis'}
          </Button>
          */}
          <Chip
            label={`Period: ${duration}`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {pdfError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setPdfError(null)}>
          {pdfError}
        </Alert>
      )}
      {emailError && (
        <Alert severity="error" sx={{ mb: 3, whiteSpace: 'pre-wrap' }} onClose={() => setEmailError(null)}>
          {emailError}
        </Alert>
      )}
      {aiError && (
        <Alert severity="error" sx={{ mb: 3, whiteSpace: 'pre-wrap' }} onClose={() => setAiError(null)}>
          {aiError}
        </Alert>
      )}
      {emailSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setEmailSuccess(false)}>
          <Box>
            <strong>✅ Report sent successfully!</strong>
            <Box sx={{ mt: 1 }}>
              <strong>Recipients:</strong> {successRecipients || 'No recipients'}
            </Box>
          </Box>
        </Alert>
      )}
      {pdfSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setPdfSuccess(false)}>
          ✅ Report downloaded successfully
        </Alert>
      )}

      {/* Email Preview and Edit Modal */}
      <Dialog
        open={emailPreviewOpen}
        onClose={() => setEmailPreviewOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
    sx: {
      height: '95vh', // 80% of viewport height
      maxHeight: '1500px' // Optional max limit
    }
  }}
      >
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 'bold' }}>
          📧 Email Preview - Review Before Sending
        </DialogTitle>
        <DialogContent sx={{ mt: 2, maxHeight: '90vh', overflow: 'auto' }}>
          {emailGenerating ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography color="textSecondary">Generating email content with AI...</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#666' }}>
                  This email will be sent to:
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {selectedRecipients.map(email => (
                    <Chip
                      key={email}
                      label={email}
                      variant="outlined"
                      color="primary"
                      size="small"
                      sx={{ mr: 1, mb: 0.5 }}
                    />
                  ))}
                  {customEmail && (
                    <Chip
                      label={customEmail}
                      variant="outlined"
                      color="secondary"
                      size="small"
                      sx={{ mr: 1, mb: 0.5 }}
                    />
                  )}
                </Box>
              </Box>
              { /*. 

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Edit Email Content:</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  value={editableEmailContent}
                  onChange={(e) => setEditableEmailContent(e.target.value)}
                  variant="outlined"
                  placeholder="Email content will appear here..."
                  sx={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
              </Box>

              */ }

              <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Preview (as it will appear):</Typography>
                <Box
                  sx={{
                    backgroundColor: 'white',
                    p: 2,
                    borderRadius: 1,
                    maxHeight: '1500px',
                    overflow: 'auto',
                    border: '1px solid #ddd'
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: editableEmailContent }} />
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setEmailPreviewOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setEmailPreviewOpen(false);
              setRecipientDialogOpen(true);
            }}
            color="secondary"
          >
            Change Recipients
          </Button>
          <Button
            onClick={handleSendEmailWithPDF}
            variant="contained"
            color="info"
            disabled={emailSending || !editableEmailContent}
          >
            {emailSending ? 'Sending...' : 'Confirm & Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recipient Selection Dialog */}
      <Dialog
        open={recipientDialogOpen}
        onClose={() => setRecipientDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 'bold' }}>
          📧 Select Email Recipients
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: '#666' }}>
            Choose who should receive this report:
          </Typography>

          {/* Predefined Recipients */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
            Team Members:
          </Typography>
          {recipientsLoading ? (
            <Box sx={{ p: 2 }}>
              <CircularProgress size={30} />
              <Typography variant="body2" sx={{ mt: 1 }}>Loading recipients...</Typography>
            </Box>
          ) : availableRecipients.length > 0 ? (
            <FormGroup sx={{ mb: 3 }}>
              {availableRecipients.map(({ email, label }) => (
                <FormControlLabel
                  key={email}
                  control={
                    <Checkbox
                      checked={selectedRecipients.includes(email)}
                      onChange={() => handleRecipientChange(email)}
                    />
                  }
                  label={`${label} (${email})`}
                />
              ))}
            </FormGroup>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              No team members available. You can add custom emails below.
            </Typography>
          )}

          {/* Custom Email Input */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Add Custom Email:
          </Typography>
          <TextField
            fullWidth
            type="email"
            label="Custom Email Address"
            placeholder="e.g., someone@example.com"
            value={customEmail}
            onChange={(e) => setCustomEmail(e.target.value)}
            size="small"
            helperText="Leave blank if not needed"
          />

          {/* Selected Recipients Summary */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Selected Recipients:
            </Typography>
            {selectedRecipients.length === 0 && !customEmail ? (
              <Typography variant="body2" color="textSecondary">
                No recipients selected
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {selectedRecipients.map(email => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => handleRecipientChange(email)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
                {customEmail && (
                  <Chip
                    label={customEmail}
                    onDelete={() => setCustomEmail('')}
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setRecipientDialogOpen(false);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRecipients}
            variant="contained"
            color="primary"
          >
            Continue to Preview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden Email Report Content for pdf - Only Activity Timeline + Filtered Maps for PDF */}
<Box
  ref={emailReportRef}
  sx={{
    position: 'absolute',
    left: -9999,
    top: -9999,
    width: '210mm',
    bgcolor: 'white',
    p: 4,
    zIndex: -1,
    '@media print': {
      position: 'static',
      left: 0,
      top: 0,
      width: '100%',
      boxShadow: 'none',
      border: 'none',
      backgroundColor: 'white !important',
      '-webkit-print-color-adjust': 'exact',
      printColorAdjust: 'exact',
     
      margin: '0 !important',
    }
  }}
  className="pdf-optimized"
  data-pdf-report="activity-report"
>
  {reportData && reportData.activityReport && (
    <>
      {/* PDF Header Section - ONLY on first page */}
      <Box 
  data-pdf-section="header"
  sx={{ 
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '1050px',
    pb: 3, 
    borderBottom: '2px solid #1976d2',
    textAlign: 'center',
    '@media print': {
      borderColor: '#000 !important',
      minHeight: '120px',
    }
  }}
>
   <Box sx={{'mt': 30 }}>
            <picture>

 
   <img src="https://cdn.prod.website-files.com/62677662b270f926409f5e9d/641b15d9cd77169dd5ab8ac9_logo-am-unternehmensberatung.svg" alt="" style={{height: "50px", width: "auto"}} />
  
   
</picture>



            
            
              </Box>

  <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
    <Typography variant="h4" sx={{ 
      fontWeight: 'bold', 
      color: '#000 !important',
      '@media print': {
        fontSize: '24pt',
      }
    }}>
      Aktivitätsbericht  
    </Typography>
  </Box>
  
  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
    <Typography variant="body2" sx={{ 
      color: '#000',
      '@media print': {
        fontSize: '11pt',
      }
    }}>
      Geschäft: {selectedBusiness?.businessName || 'Business'}
    </Typography>

    <Typography variant="body2" sx={{ 
      color: '#000',
      '@media print': {
        fontSize: '11pt',
      }
    }}>. 
    
      Zeitraum: {formatToGermanDate(dateRange.startDate)} bis {formatToGermanDate(dateRange.endDate)}
    </Typography>

     <Typography variant="body2" sx={{ 
      color: '#000',
      '@media print': {
        fontSize: '11pt',
      }
    }}>
       Erstellt: {new Date().toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' })}
    </Typography>
  </Box>
  

</Box>

      {/* Activity Timeline Chart Section */}
      <Box 
        data-pdf-section="activity-chart"
        sx={{ 
          mb: 4,
          '@media print': {
            marginBottom: '0',
            height: 'auto',
          }
        }}
      >
        <Typography variant="h6" sx={{ 
          fontWeight: 'bold', 
          mb: 2, 
          ml:5,
          color: '#000',
          '@media print': {
            fontSize: '16pt',
          }
        }}>
         ZEITLINIE DER AKTIVITÄTEN SEIT ANLEGUNG 
        </Typography>

         <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            
           
            overflowX: 'auto',
            '@media print': {
              flexWrap: 'wrap',
              overflowX: 'visible',
            },
            '& > *': {
              flex: '1 1 0px',
              minWidth: '100px',
              '@media print': {
                boxShadow: 'none',
                border: '1px solid #ddd',
                backgroundColor: 'white !important',
              }
            }
          }}>
            <Card className="pdf-table-container">
             <CardContent sx={{
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    p: 2,
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      borderColor: '#ff9800',
    },
    '@media print': {
      border: '1px solid #ddd !important',
       boxShadow: '0 1px 3px rgba(0,0,0,0.2) !important',
      backgroundColor: 'white !important',
      '-webkit-print-color-adjust': 'exact',
      printColorAdjust: 'exact',

    }
  }}>
                <Typography variant="caption" sx={{ 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  color: '#000',
                  '@media print': {
                    fontSize: '9pt',
                  }
                }}>BEITRÄGE IN DIESEM ZEITRAUM</Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  color: '#2196f3', 
               
                  '@media print': {
                    color: '#000 !important',
                    fontSize: '14pt',
                  }
                }}>
                  {reportData.activityReport.summary.postsCreatedThisPeriod || 0}
                </Typography>
              </CardContent>
            </Card>
            <Card className="pdf-table-container">
              <CardContent sx={{
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    p: 2,
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      borderColor: '#ff9800',
    },
    '@media print': {
      border: '1px solid #ddd !important',
       boxShadow: '0 1px 3px rgba(0,0,0,0.2) !important',
      backgroundColor: 'white !important',
      '-webkit-print-color-adjust': 'exact',
      printColorAdjust: 'exact',

    }
  }}>
                <Typography variant="caption" sx={{ 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  color: '#000',
                  '@media print': {
                    fontSize: '9pt',
                  }
                }}>GESAMTBEITRÄGE SEIT ANLEGUNG</Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  color: '#2196f3', 
               
                  '@media print': {
                    color: '#000 !important',
                    fontSize: '14pt',
                  }
                }}>
                  {reportData.activityReport.summary.postsCreatedAllTime || 0}
                </Typography>
              </CardContent>
            </Card>
            <Card className="pdf-table-container">
              <CardContent sx={{
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    p: 2,
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      borderColor: '#ff9800',
    },
    '@media print': {
      border: '1px solid #ddd !important',
       boxShadow: '0 1px 3px rgba(0,0,0,0.2) !important',
      backgroundColor: 'white !important',
      '-webkit-print-color-adjust': 'exact',
      printColorAdjust: 'exact',

    }
  }}>
                <Typography variant="caption" sx={{ 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  color: '#000',
                  '@media print': {
                    fontSize: '9pt',
                  }
                }}>MEDIEN IN DIESEM ZEITRAUM</Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  color: '#ff9800', 
              
                  '@media print': {
                    color: '#000 !important',
                    fontSize: '14pt',
                  }
                }}>
                  {reportData.activityReport.summary.mediasAddedThisPeriod || 0}
                </Typography>
              </CardContent>
            </Card>
            <Card className="pdf-table-container">
              <CardContent sx={{
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    p: 2,
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      borderColor: '#ff9800',
    },
    '@media print': {
      border: '1px solid #ddd !important',
       boxShadow: '0 1px 3px rgba(0,0,0,0.2) !important',
      backgroundColor: 'white !important',
      '-webkit-print-color-adjust': 'exact',
      printColorAdjust: 'exact',

    }
  }}>
                <Typography variant="caption" sx={{ 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  color: '#000',
                  '@media print': {
                    fontSize: '9pt',
                  }
                }}>GESAMTMEDIEN SEIT ANLEGUNG</Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  color: '#f57c00', 
               
                  '@media print': {
                    color: '#000 !important',
                    fontSize: '14pt',
                  }
                }}>
                  {reportData.activityReport.summary.mediasAddedAllTime || 0}
                </Typography>
              </CardContent>
            </Card>
            <Card>
                 <CardContent sx={{
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    p: 2,
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      borderColor: '#ff9800',
    },
    '@media print': {
      border: '1px solid #ddd !important',
       boxShadow: '0 1px 3px rgba(0,0,0,0.2) !important',
      backgroundColor: 'white !important',
      '-webkit-print-color-adjust': 'exact',
      printColorAdjust: 'exact',

    }
  }}>
                    <Typography variant="caption" sx={{ 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  color: '#000',
                  '@media print': {
                    fontSize: '9pt',
                  }
                }}>
                     EINBLENDUNGEN IN DIESEM ZEITRAUM
                    </Typography>
                    <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  color: '#f57c00', 
               
                  '@media print': {
                    color: '#000 !important',
                    fontSize: '14pt',
                  }
                }}>
                      {(reportData.stats?.impressions?.current || 0).toLocaleString()}
                    </Typography>
                    
                  </CardContent>
                </Card>

            <Card>
                  <CardContent sx={{
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    p: 2,
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      borderColor: '#ff9800',
    },
    '@media print': {
      border: '1px solid #ddd !important',
       boxShadow: '0 1px 3px rgba(0,0,0,0.2) !important',
      backgroundColor: 'white !important',
      '-webkit-print-color-adjust': 'exact',
      printColorAdjust: 'exact',

    }
  }}>
                    <Typography variant="caption" sx={{ 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  color: '#000',
                  '@media print': {
                    fontSize: '9pt',
                  }
                }}>
                    GESAMTEINBLENDUNGEN SEIT ANLEGUNG
                    </Typography>
                     <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  color: '#f57c00', 
               
                  '@media print': {
                    color: '#000 !important',
                    fontSize: '14pt',
                  }
                }}>
                      {(
                        (reportData.allTimeInsights.summaryStats['BUSINESS_IMPRESSIONS_DESKTOP_SEARCH']?.total || 0) +
                        (reportData.allTimeInsights.summaryStats['BUSINESS_IMPRESSIONS_MOBILE_SEARCH']?.total || 0) +
                        (reportData.allTimeInsights.summaryStats['BUSINESS_IMPRESSIONS_DESKTOP_MAPS']?.total || 0) +
                        (reportData.allTimeInsights.summaryStats['BUSINESS_IMPRESSIONS_MOBILE_MAPS']?.total || 0)
                      ).toLocaleString()}
                    </Typography>
                   
                  </CardContent>
                </Card>

                
          </Box>

        <Paper sx={{ p: 2,mt:-2 }}>
          <CardHeader title="Kumulative Einblendungen, Beiträge und Medien im Laufe der Zeit" />
          <Box sx={{
            height: 400,
            '@media print': {
              height: '600px',
              pageBreakInside: 'avoid'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              {reportData.cumulativeImpressionsChartData && reportData.cumulativeImpressionsChartData.length > 0 ? (
                <LineChart data={reportData.cumulativeImpressionsChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    interval={Math.max(0, Math.floor(reportData.cumulativeImpressionsChartData.length / 12))}
                  />
                  <YAxis />
                  <Tooltip formatter={(value) => value.toLocaleString()} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="Cumulative Einblendungen"
                    stroke="#2196f3"
                    strokeWidth={2}
                    name="Cumulative Einblendungen"
                    dot={{ fill: '#2196f3', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              ) : (
                <Typography color="textSecondary">No cumulative impressions data available</Typography>
              )}
            </ResponsiveContainer>
          </Box>
        </Paper>
        
        <Paper sx={{ 
          mb: 4, 
          p: 1,
          '@media print': {
            boxShadow: 'none',
            border: '1px solid #ddd !important',
            backgroundColor: 'white !important',
          }
        }}>
         

          <Box sx={{ 
            height: 400,
            '@media print': {
              height: '300px !important',
            }
          }}>
            <ResponsiveContainer 
              width="100%" 
              height="100%"
              className="recharts-responsive-container"
            >
              <LineChart data={reportData.activityReport.chartData}>
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="#ddd"
                />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  interval={Math.floor(reportData.activityReport.chartData.length / 7) || 0}
                  stroke="#000"
                />
                <YAxis stroke="#000" />
                <Tooltip 
                  formatter={(value) => value.toLocaleString()}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    color: '#000'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="posts" 
                  stroke="#2196f3" 
                  strokeWidth={2} 
                  name="Cumulative Beiträge" 
                />
                <Line 
                  type="monotone" 
                  dataKey="media" 
                  stroke="#ff9800" 
                  strokeWidth={2} 
                  name="Cumulative Medien" 
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>

      {/* Each SplitMapComparison on SEPARATE page */}
      {reportData.allKeywords && reportData.allKeywords.map((keyword, index) => {
        if (!rankingHistory || rankingHistory.length === 0) {
          return null;
        }

        const baselineSnapshot = rankingHistory[0];
        const comparisonSnapshot = rankingHistory[rankingHistory.length - 1];

        if (!baselineSnapshot || !comparisonSnapshot) {
          return null;
        }

        const findKeywordInSnapshot = (snapshot, keywordName) => {
          if (!snapshot?.keywords) return null;
          let found = snapshot.keywords.find(k => k.keyword === keywordName);
          if (!found) {
            found = snapshot.keywords.find(k => k.keyword?.toLowerCase() === keywordName?.toLowerCase());
          }
          return found;
        };

        // Get keyword-specific baseline and comparison snapshots from ALL snapshots
        const getKeywordSnapshotPair = (keywordName, snapshots, startDate, endDate) => {
          const normalizedKeyword = keywordName?.toLowerCase();
          const dateRangeStart = new Date(startDate);
          const dateRangeEnd = new Date(endDate);

          let baselineSnapshot = null;
          let comparisonSnapshot = null;

          // Filter snapshots in date range and find first/last with keyword
          for (const snapshot of snapshots) {
            const snapshotDate = new Date(snapshot.analyzedAt || snapshot.snapshotDate);
            const isInRange = snapshotDate >= dateRangeStart && snapshotDate <= dateRangeEnd;

            if (!isInRange) continue;

            if (snapshot?.keywords) {
              const foundKeyword = snapshot.keywords.find(k => k.keyword?.toLowerCase() === normalizedKeyword);
              if (foundKeyword) {
                if (!baselineSnapshot) {
                  // First snapshot with this keyword
                  baselineSnapshot = snapshot;
                }
                // Always update comparison to get the last one
                comparisonSnapshot = snapshot;
              }
            }
          }

          // Return pair of snapshots for this keyword (baseline and comparison)
          if (baselineSnapshot && comparisonSnapshot) {
            return [baselineSnapshot, comparisonSnapshot];
          }

          // Fallback to global ranking history if keyword-specific pair not found
          return rankingHistory;
        };

        let filteredSnapshotsForMap = getKeywordSnapshotPair(keyword.keyword, allRankingSnapshots, dateRange.startDate, dateRange.endDate);
        let isFiltered = false;

        if (reportData.regionalAnalysis && reportData.regionalAnalysis.keywords) {
          const regionalKeywordData = reportData.regionalAnalysis.keywords.find(
            rk => rk.keyword?.toLowerCase() === keyword.keyword?.toLowerCase()
          );

          if (regionalKeywordData && regionalKeywordData.improvements > 0) {
            const regionsWithProgress = new Set();
            if (regionalKeywordData.regionalKeywords) {
              regionalKeywordData.regionalKeywords
                .filter(rk => rk.improvement > 0)
                .forEach(rk => {
                  const parts = rk.keyword?.split(' ') || [];
                  if (parts.length > 1) {
                    regionsWithProgress.add(parts[parts.length - 1].toLowerCase());
                  }
                });
            }

            if (regionsWithProgress.size > 0) {
              const tempFilteredSnapshots = rankingHistory.map(snapshot => ({
                ...snapshot,
                keywords: snapshot.keywords?.map(kw => {
                  if (kw.keyword?.toLowerCase() === keyword.keyword?.toLowerCase()) {
                    return {
                      ...kw,
                      testLocations: kw.testLocations?.filter(loc => {
                        const locationCity = loc.city?.toLowerCase() || '';
                        return Array.from(regionsWithProgress).some(region =>
                          locationCity.includes(region)
                        );
                      }) || []
                    };
                  }
                  return kw;
                }) || []
              }));

              const hasFilteredLocations = tempFilteredSnapshots.some(snap =>
                snap.keywords?.some(kw =>
                  kw.keyword?.toLowerCase() === keyword.keyword?.toLowerCase() &&
                  (kw.testLocations?.length || 0) > 0
                )
              );

              if (hasFilteredLocations) {
                filteredSnapshotsForMap = tempFilteredSnapshots;
                isFiltered = true;
              }
            }
          }
        }

        const snapshotPairForMap = filteredSnapshotsForMap;

        const hasFilteredLocations = snapshotPairForMap.some(snap =>
          snap.keywords?.some(kw =>
            kw.keyword?.toLowerCase() === keyword.keyword?.toLowerCase() &&
            (kw.testLocations?.length || 0) > 0
          )
        );

        let filteredLocationCount = 0;
        if (snapshotPairForMap.length > 0) {
          const lastSnapshot = snapshotPairForMap[snapshotPairForMap.length - 1];
          const keywordInSnapshot = lastSnapshot.keywords?.find(
            kw => kw.keyword?.toLowerCase() === keyword.keyword?.toLowerCase()
          );
          filteredLocationCount = keywordInSnapshot?.testLocations?.length || 0;
        }

        if (!hasFilteredLocations || filteredLocationCount === 0) {
          return null;
        }

        const emailBaselineSnapshot = snapshotPairForMap[0];
        const emailComparisonSnapshot = snapshotPairForMap[snapshotPairForMap.length - 1];

        const emailBaselineKeyword = emailBaselineSnapshot?.keywords?.find(
          kw => kw.keyword?.toLowerCase() === keyword.keyword?.toLowerCase()
        );
        const emailComparisonKeyword = emailComparisonSnapshot?.keywords?.find(
          kw => kw.keyword?.toLowerCase() === keyword.keyword?.toLowerCase()
        );

        if (!emailBaselineKeyword || !emailComparisonKeyword) {
          return null;
        }

        const emailBaselineLocations = emailBaselineKeyword.testLocations || [];
        const emailComparisonLocations = emailComparisonKeyword.testLocations || [];

        const emailGreenLocations = emailComparisonLocations
          .map((compLoc, idx) => {
            const baseLoc = emailBaselineLocations[idx];
            if (!baseLoc) return null;

            const rankChange = baseLoc.rank - compLoc.rank;
            if (rankChange <= 0) return null;

            return compLoc;
          })
          .filter(loc => loc !== null);

        if (emailGreenLocations.length === 0) {
          return null;
        }

        const pairedLocations = {
          baseline: [],
          comparison: []
        };

        emailComparisonLocations.forEach((compLoc, idx) => {
          const baseLoc = emailBaselineLocations[idx];
          if (!baseLoc) return;
          pairedLocations.baseline.push(baseLoc);
          pairedLocations.comparison.push(compLoc);
        });

        const filteredRankingHistory = [emailBaselineSnapshot, emailComparisonSnapshot]
          .filter(Boolean)
          .map((snapshot, idx) => ({
            ...snapshot,
            keywords: snapshot.keywords?.map(kw => {
              if (kw.keyword?.toLowerCase() === keyword.keyword?.toLowerCase()) {
                return {
                  ...kw,
                  testLocations: idx === 0 ? pairedLocations.baseline : pairedLocations.comparison
                };
              }
              return kw;
            }) || []
          }));

        return (
          <Box
            key={keyword.keyword}
            className="pdf-map-container"
            data-pdf-map={index}
            data-pdf-map-index={index}
            sx={{
              mb: 3,
              height: '257mm',
              minHeight: '257mm',
              width: '100%',
              '@media print': {
                // Force new page for each map
                pageBreakBefore: 'always !important',
                breakBefore: 'page !important',
                // Prevent breaking inside
                pageBreakInside: 'avoid !important',
                breakInside: 'avoid !important',
                // Fixed height for A4 page
                height: '257mm !important',
                minHeight: '257mm !important',
                maxHeight: '257mm !important',
                width: '100%',
                margin: '0',
                padding: '0',
                overflow: 'hidden',
                position: 'relative',
              }
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                border: '1px solid #ddd',
                borderRadius: 1,
                overflow: 'hidden',
                '@media print': {
                  border: 'none',
                  borderRadius: 0,
                  width: '100%',
                  height: '100%',
                }
              }}
            >
              <SplitMapComparison
                selectedKeyword={keyword.keyword}
                rankingHistory={filteredRankingHistory}
                businessLocation={selectedBusiness}
                onCreateSnapshot={() => {}}
                loading={false}
                Inpdf={true}
              />
            </Box>
          </Box>
        );
      })}
    </>
  )}
</Box>

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography color="textSecondary">Generating report...</Typography>
        </Box>
      ) : reportData ? (
        <Box ref={reportRef} sx={{ bgcolor: 'white', p: 2 }}>

          {/* Charts */}
          <Box sx={{ pageBreakInside: 'avoid', mb: 4 }} data-pdf-section="performance-trends">
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Performance Trends
            </Typography>

            {/* Period KPI Cards */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              gap: 2,
              mb: 3
            }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    IMPRESSIONS FOR THE PERIOD
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196f3', mb: 1 }}>
                    {(reportData.stats?.impressions?.current || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(dateRange.startDate).toLocaleDateString()} - {new Date(dateRange.endDate).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    CALLS FOR THE PERIOD
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50', mb: 1 }}>
                    {(reportData.stats?.calls?.current || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(dateRange.startDate).toLocaleDateString()} - {new Date(dateRange.endDate).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    WEBSITE CLICKS FOR THE PERIOD
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 1 }}>
                    {(reportData.stats?.websiteClicks?.current || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(dateRange.startDate).toLocaleDateString()} - {new Date(dateRange.endDate).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Paper sx={{ mb: 2, p: 2 }}>
              <CardHeader title="KPI Trends For The Period" />
            <Box sx={{
              height: 500,
              '@media print': {
                height: '700px',
                pageBreakInside: 'avoid'
              }
            }}>
              <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={reportData.chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      interval={Math.floor(reportData.chartData.length / 7) || 0}
                    />
                    <YAxis />
                    <Tooltip formatter={(value) => value.toLocaleString()} />
                    <Legend />
                    <Line type="monotone" dataKey="impressions" stroke="#2196f3" strokeWidth={2} name="Impressions" />
                    <Line type="monotone" dataKey="calls" stroke="#4caf50" strokeWidth={2} name="Calls" />
                    <Line type="monotone" dataKey="websiteClicks" stroke="#ff9800" strokeWidth={2} name="Website Clicks" />
                  </LineChart>
                </ResponsiveContainer>
            </Box>
          </Paper>
          </Box>

          {/* All-Time Performance Metrics Bar Chart */}
          {reportData.allTimeInsights && reportData.allTimeInsights.timeSeries && (
            <Box sx={{ pageBreakInside: 'avoid', mb: 4 }} data-pdf-section="all-time-metrics">
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Performance Metrics Since Business Added ({reportData.activityReport?.summary?.dateRange?.businessCreatedAt ? new Date(reportData.activityReport.summary.dateRange.businessCreatedAt).toLocaleDateString() : 'N/A'} - {new Date().toLocaleDateString()})
              </Typography>

              {/* KPI Cards */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr'},
                gap: 2,
                mb: 3
              }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      TOTAL IMPRESSIONS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196f3', mb: 1 }}>
                      {(
                        (reportData.allTimeInsights.summaryStats['BUSINESS_IMPRESSIONS_DESKTOP_SEARCH']?.total || 0) +
                        (reportData.allTimeInsights.summaryStats['BUSINESS_IMPRESSIONS_MOBILE_SEARCH']?.total || 0) +
                        (reportData.allTimeInsights.summaryStats['BUSINESS_IMPRESSIONS_DESKTOP_MAPS']?.total || 0) +
                        (reportData.allTimeInsights.summaryStats['BUSINESS_IMPRESSIONS_MOBILE_MAPS']?.total || 0)
                      ).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Since {reportData.activityReport?.summary?.dateRange?.businessCreatedAt ? new Date(reportData.activityReport.summary.dateRange.businessCreatedAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>

                

                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      TOTAL CALLS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50', mb: 1 }}>
                      {(reportData.allTimeInsights.summaryStats['CALL_CLICKS']?.total || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Since {reportData.activityReport?.summary?.dateRange?.businessCreatedAt ? new Date(reportData.activityReport.summary.dateRange.businessCreatedAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>

                

                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      TOTAL WEBSITE CLICKS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 1 }}>
                      {(reportData.allTimeInsights.summaryStats['WEBSITE_CLICKS']?.total || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Since {reportData.activityReport?.summary?.dateRange?.businessCreatedAt ? new Date(reportData.activityReport.summary.dateRange.businessCreatedAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>

               
              </Box>

              <Paper sx={{ mb: 2, p: 2 }}>
                <CardHeader title="Kumulative Einblendungen im Laufe der Zeit" />
                <Box sx={{
                  height: 400,
                  '@media print': {
                    height: '600px',
                    pageBreakInside: 'avoid'
                  }
                }}>
                  <ResponsiveContainer width="100%" height="100%">
                    {reportData.cumulativeImpressionsChartData && reportData.cumulativeImpressionsChartData.length > 0 ? (
   <LineChart data={reportData.cumulativeImpressionsChartData}>
  <CartesianGrid strokeDasharray="3 3" />
  <XAxis
    dataKey="date"
    tick={{ fontSize: 12 }}
    interval={Math.max(0, Math.floor(reportData.cumulativeImpressionsChartData.length / 12))}
    tickFormatter={(value) => {
      // Convert date to MM/YY format
      try {
        const date = new Date(value);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(2); // Last 2 digits of year
        return `${month}/${year}`;
      } catch (error) {
        return value;
      }
    }}
  />
  <YAxis />
  <Tooltip 
    formatter={(value) => value.toLocaleString()}
    labelFormatter={(label) => {
      // Format tooltip label as well
      try {
        const date = new Date(label);
        return date.toLocaleDateString('de-DE'); // German date format for tooltip
      } catch (error) {
        return label;
      }
    }}
  />
  <Legend />
  <Line
    type="monotone"
    dataKey="Cumulative Einblendungen"
    stroke="#2196f3"
    strokeWidth={2}
    name="Cumulative Einblendungen"
    dot={{ fill: '#2196f3', r: 4 }}
    activeDot={{ r: 6 }}
  />
</LineChart>
                    ) : (
                      <Typography color="textSecondary">No cumulative impressions data available</Typography>
                    )}
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Activity Report: Posts & Media Chart (GMB Data) */}
          {reportData.activityReport && reportData.activityReport.summary && (
            <Box sx={{ pageBreakInside: 'avoid', mb: 4 }} data-pdf-section="activity-report">
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, mt: 3 }}>
                Activity Timeline (Posts, Media) - Since {new Date(reportData.activityReport.summary.dateRange.businessCreatedAt).toLocaleDateString()}
              </Typography>
              <Paper sx={{ mb: 4, p: 2 }}>
                {reportData.activityReport.chartData && reportData.activityReport.chartData.length === 0 && (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Alert severity="info">
                      <Typography>No posts or media data available for the selected period.</Typography>
                    </Alert>
                  </Box>
                )}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
                  gap: 2,
                  mb: 3
                }}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.75rem', fontWeight: 600 }}>POSTS CREATED THIS PERIOD</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196f3', mb: 1 }}>
                        {reportData.activityReport.summary.postsCreatedThisPeriod || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(reportData.activityReport.summary.dateRange.reportStart).toLocaleDateString()} - {new Date(reportData.activityReport.summary.dateRange.reportEnd).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.75rem', fontWeight: 600 }}>POSTS CREATED SINCE BUSINESS ADDED</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                        {reportData.activityReport.summary.postsCreatedAllTime || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Since {new Date(reportData.activityReport.summary.dateRange.businessCreatedAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.75rem', fontWeight: 600 }}>MEDIAS ADDED THIS PERIOD</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 1 }}>
                        {reportData.activityReport.summary.mediasAddedThisPeriod || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(reportData.activityReport.summary.dateRange.reportStart).toLocaleDateString()} - {new Date(reportData.activityReport.summary.dateRange.reportEnd).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.75rem', fontWeight: 600 }}>MEDIAS ADDED SINCE BUSINESS ADDED</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 1 }}>
                        {reportData.activityReport.summary.mediasAddedAllTime || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Since {new Date(reportData.activityReport.summary.dateRange.businessCreatedAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                {reportData.activityReport.chartData && reportData.activityReport.chartData.length > 0 ? (
                  <Box sx={{
                    height: 400,
                    '@media print': {
                      height: '500px',
                      pageBreakInside: 'avoid',
                      width:"100%"

                    }
                  }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={reportData.activityReport.chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 12 }}
                          interval={Math.floor(reportData.activityReport.chartData.length / 7) || 0}
                        />
                        <YAxis />
                        <Tooltip
                          formatter={(value) => value.toLocaleString()}
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="posts" stroke="#2196f3" strokeWidth={2} name="Cumulative Posts" />
                        <Line type="monotone" dataKey="media" stroke="#ff9800" strokeWidth={2} name="Cumulative Media" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography>No chart data available. Ensure posts or media have been created.</Typography>
                  </Alert>
                )}
              </Paper>
            </Box>
          )}

          {/* KPI Comparison Table */}
          <Box sx={{ pageBreakInside: 'avoid', mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
               KPI Comparison
            </Typography>
            <Paper sx={{ mb: 4 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Metric</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Previous Period</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Current Period</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Change</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Trend</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    {
                      name: 'Impressions',
                      previous: reportData.stats.impressions.previous,
                      current: reportData.stats.impressions.current
                    },
                    {
                      name: 'Calls',
                      previous: reportData.stats.calls.previous,
                      current: reportData.stats.calls.current
                    },
                    {
                      name: 'Website Clicks',
                      previous: reportData.stats.websiteClicks.previous,
                      current: reportData.stats.websiteClicks.current
                    },
                    {
                      name: 'Posts',
                      previous: reportData.activities.postsCount.previous,
                      current: reportData.activities.postsCount.current
                    },
                    {
                      name: 'Media/Photos',
                      previous: 0,
                      current: reportData.activities.mediasCount
                    },
                    {
                      name: 'Reviews',
                      previous: 0,
                      current: reportData.activities.reviewsCount
                    },
                    (reportData.serviceMetrics.added.current > 0 || reportData.serviceMetrics.added.previous > 0) && {
                      name: 'Services Added',
                      previous: reportData.serviceMetrics.added.previous,
                      current: reportData.serviceMetrics.added.current
                    },
                    (reportData.serviceMetrics.deleted.current > 0 || reportData.serviceMetrics.deleted.previous > 0) && {
                      name: 'Services Deleted',
                      previous: reportData.serviceMetrics.deleted.previous,
                      current: reportData.serviceMetrics.deleted.current
                    },
                    (reportData.serviceMetrics.descriptionAdded.current > 0 || reportData.serviceMetrics.descriptionAdded.previous > 0) && {
                      name: 'Descriptions Added',
                      previous: reportData.serviceMetrics.descriptionAdded.previous,
                      current: reportData.serviceMetrics.descriptionAdded.current
                    },
                    (reportData.serviceMetrics.descriptionUpdated.current > 0 || reportData.serviceMetrics.descriptionUpdated.previous > 0) && {
                      name: 'Descriptions Updated',
                      previous: reportData.serviceMetrics.descriptionUpdated.previous,
                      current: reportData.serviceMetrics.descriptionUpdated.current
                    }
                  ].filter(Boolean).map((metric, index) => {
                    const change = metric.current - metric.previous;
                    const changeColor = change > 0 ? '#4caf50' : change < 0 ? '#f44336' : '#1976d2';
                    const trendIcon = change > 0 ? <TrendingUp sx={{ color: '#4caf50' }} /> : change < 0 ? <TrendingDown sx={{ color: '#f44336' }} /> : null;

                    return (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {metric.name}
                        </TableCell>
                        <TableCell align="center">
                          {metric.previous.toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          {metric.current.toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                                color: changeColor
                              }}
                            >
                              {metric.previous} → {metric.current}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            {trendIcon}
                            <Chip
                              label={`${change > 0 ? '+' : ''}${change}`}
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                color: '#fff',
                                backgroundColor: changeColor
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          </Box>

          {/* Keyword Ranking Changes */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Keywords  Overview
          </Typography>
          <Grid container spacing={2} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Rankings Improved
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {reportData.keywordMetrics.improved}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Keywords with better ranking
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Rankings Declined
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    {reportData.keywordMetrics.declined}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Keywords with worse ranking
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    No Change
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                    {reportData.keywordMetrics.nochange}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Keywords with same ranking
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Top Keywords Table */}
          {reportData.allKeywords.length > 0 && (
            <>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                 Keywords Ranking Analysis
              </Typography>

              

              {/* Ranking Snapshot Comparison - All Keywords with Auto-Selected Dates */}
              {reportData.allKeywords && reportData.allKeywords.length > 0 && rankingHistory && rankingHistory.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Card>
                    <CardHeader
                      title="Ranking Snapshot Comparison"
                      subheader={`Comparing ${rankingHistory.length} snapshot${rankingHistory.length !== 1 ? 's' : ''} - All ${reportData.allKeywords.length} tracked keywords`}
                    />
                    <Divider />
                    <CardContent>
                     
                      {/* Info Bar: Date Range and Snapshot Count */}
                      <Box sx={{
                        mb: 3,
                        p: 2,
                        backgroundColor: '#f5f9ff',
                        borderLeft: '4px solid #2196f3',
                        borderRadius: 1
                      }}>
                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              Baseline:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                              {rankingHistory.length > 0 && new Date(rankingHistory[0].analyzedAt || rankingHistory[0].snapshotDate).toLocaleDateString('de-DE')}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              Comparison:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                              {rankingHistory.length > 0 && new Date(rankingHistory[rankingHistory.length - 1].analyzedAt || rankingHistory[rankingHistory.length - 1].snapshotDate).toLocaleDateString('de-DE')}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', ml: 'auto' }}>
                            Total: {rankingHistory.length} Snapshots
                          </Typography>
                        </Box>
                      </Box>

                      {/* Keywords List - One per Row */}
                      <Stack spacing={3}>
                        {reportData.allKeywords && reportData.allKeywords.map((keyword) => {
                          // Get keyword-specific baseline and comparison snapshots
                          // Ensure allRankingSnapshots is valid before accessing
                          if (!allRankingSnapshots || allRankingSnapshots.length === 0) {
                            return null;
                          }

                          // Use keyword-specific snapshots (earliest and latest for this keyword)
                          const normalizedKeyword = keyword.keyword?.toLowerCase();
                          const dateRangeStart = new Date(dateRange.startDate);
                          const dateRangeEnd = new Date(dateRange.endDate);

                          let baselineSnapshot = null;
                          let comparisonSnapshot = null;

                          // Find first and last snapshot with this keyword
                          for (const snapshot of allRankingSnapshots) {
                            const snapshotDate = new Date(snapshot.analyzedAt || snapshot.snapshotDate);
                            const isInRange = snapshotDate >= dateRangeStart && snapshotDate <= dateRangeEnd;

                            if (!isInRange) continue;

                            if (snapshot?.keywords) {
                              const foundKeyword = snapshot.keywords.find(k => k.keyword?.toLowerCase() === normalizedKeyword);
                              if (foundKeyword) {
                                if (!baselineSnapshot) baselineSnapshot = snapshot;
                                comparisonSnapshot = snapshot;
                              }
                            }
                          }

                          // Fallback to global ranking history if not found
                          if (!baselineSnapshot || !comparisonSnapshot) {
                            if (rankingHistory && rankingHistory.length > 0) {
                              baselineSnapshot = rankingHistory[0];
                              comparisonSnapshot = rankingHistory[rankingHistory.length - 1];
                            } else {
                              return null;
                            }
                          }

                          // Additional safety check
                          if (!baselineSnapshot || !comparisonSnapshot) {
                            return null;
                          }

                          // Find keyword with case-insensitive matching
                          const findKeywordInSnapshot = (snapshot, keywordName) => {
                            if (!snapshot?.keywords) return null;
                            // First try exact match
                            let found = snapshot.keywords.find(k => k.keyword === keywordName);
                            // If not found, try case-insensitive match
                            if (!found) {
                              found = snapshot.keywords.find(k => k.keyword?.toLowerCase() === keywordName?.toLowerCase());
                            }
                            return found;
                          };

                          const baselineKeywordData = findKeywordInSnapshot(baselineSnapshot, keyword.keyword);
                          const comparisonKeywordData = findKeywordInSnapshot(comparisonSnapshot, keyword.keyword);

                          // Get ranks from snapshot data only (not from overall report)
                          const baselineRank = baselineKeywordData?.averageRank;
                          const comparisonRank = comparisonKeywordData?.averageRank;

                          // Filter test locations based on regional keywords with positive progress
                          // Use keyword-specific snapshot pair for map comparison
                          let filteredSnapshotsForMap = [baselineSnapshot, comparisonSnapshot];
                          let isFiltered = false;

                          if (reportData.regionalAnalysis && reportData.regionalAnalysis.keywords) {
                            const regionalKeywordData = reportData.regionalAnalysis.keywords.find(
                              rk => rk.keyword?.toLowerCase() === keyword.keyword?.toLowerCase()
                            );

                            if (regionalKeywordData && regionalKeywordData.improvements > 0) {
                              // Get regions with positive progress for this keyword
                              const regionsWithProgress = new Set();
                              if (regionalKeywordData.regionalKeywords) {
                                regionalKeywordData.regionalKeywords
                                  .filter(rk => rk.improvement > 0)
                                  .forEach(rk => {
                                    // Extract region/city from regional keyword (e.g., "Markisen Berlin" -> "Berlin")
                                    const parts = rk.keyword?.split(' ') || [];
                                    if (parts.length > 1) {
                                      regionsWithProgress.add(parts[parts.length - 1].toLowerCase());
                                    }
                                  });
                              }

                              // Filter snapshots to only show test locations from regions with positive progress
                              if (regionsWithProgress.size > 0) {
                                const tempFilteredSnapshots = rankingHistory.map(snapshot => ({
                                  ...snapshot,
                                  keywords: snapshot.keywords?.map(kw => {
                                    if (kw.keyword?.toLowerCase() === keyword.keyword?.toLowerCase()) {
                                      return {
                                        ...kw,
                                        testLocations: kw.testLocations?.filter(loc => {
                                          const locationCity = loc.city?.toLowerCase() || '';
                                          return Array.from(regionsWithProgress).some(region =>
                                            locationCity.includes(region)
                                          );
                                        }) || []
                                      };
                                    }
                                    return kw;
                                  }) || []
                                }));

                                // Check if filtered snapshots have locations
                                const hasFilteredLocations = tempFilteredSnapshots.some(snap =>
                                  snap.keywords?.some(kw =>
                                    kw.keyword?.toLowerCase() === keyword.keyword?.toLowerCase() &&
                                    (kw.testLocations?.length || 0) > 0
                                  )
                                );

                                // Only use filtered snapshots if at least one location remains
                                if (hasFilteredLocations) {
                                  filteredSnapshotsForMap = tempFilteredSnapshots;
                                  isFiltered = true;
                                }
                              }
                            }
                          }

                          // Pass filtered or original snapshots for comparison
                          const snapshotPairForMap = filteredSnapshotsForMap;

                          // Check if snapshots have test locations
                          const hasFilteredLocations = snapshotPairForMap.some(snap =>
                            snap.keywords?.some(kw =>
                              kw.keyword?.toLowerCase() === keyword.keyword?.toLowerCase() &&
                              (kw.testLocations?.length || 0) > 0
                            )
                          );

                          // Calculate location count for this keyword
                          let filteredLocationCount = 0;
                          if (snapshotPairForMap.length > 0) {
                            const lastSnapshot = snapshotPairForMap[snapshotPairForMap.length - 1];
                            const keywordInSnapshot = lastSnapshot.keywords?.find(
                              kw => kw.keyword?.toLowerCase() === keyword.keyword?.toLowerCase()
                            );
                            filteredLocationCount = keywordInSnapshot?.testLocations?.length || 0;
                          }

                          const originalLocationCount = keyword.testLocations?.length || 0;

                          return (
                            <Card key={keyword.keyword} sx={{ pageBreakBefore: 'always', pageBreakInside: 'avoid' }}>
                              {/* Keyword Header - One Row */}
                              <CardHeader
                                title={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
                                      {keyword.keyword}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                                      {/* Baseline Rank */}
                                      <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>
                                          {baselineSnapshot && (new Date(baselineSnapshot.analyzedAt || baselineSnapshot.snapshotDate)).toLocaleDateString('de-DE')}
                                        </Typography>
                                        <Chip
                                          label={`#${baselineRank?.toFixed(1) || 'N/A'}`}
                                          color="default"
                                          size="small"
                                          sx={{ fontWeight: 'bold' }}
                                          variant="outlined"
                                        />
                                      </Box>

                                      {/* Arrow */}
                                      <Typography sx={{ fontWeight: 'bold', color: '#999' }}>→</Typography>

                                      {/* Comparison Rank */}
                                      <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>
                                          {comparisonSnapshot && (new Date(comparisonSnapshot.analyzedAt || comparisonSnapshot.snapshotDate)).toLocaleDateString('de-DE')}
                                        </Typography>
                                        <Chip
                                          label={`#${comparisonRank?.toFixed(1) || 'N/A'}`}
                                          color={comparisonRank && baselineRank ? (comparisonRank < baselineRank ? 'success' : comparisonRank > baselineRank ? 'error' : 'default') : 'default'}
                                          size="small"
                                          sx={{ fontWeight: 'bold' }}
                                          variant="outlined"
                                        />







                                      </Box>
                                    </Box>
                                  </Box>
                                }
                                subheader={
                                  isFiltered
                                    ? `🎯 ${filteredLocationCount}/${originalLocationCount} test locations with positive progress`
                                    : `${originalLocationCount} test locations`
                                }
                                sx={{ pb: 1 }}
                              />
                              <Divider />
                              {isFiltered && (
                                <Alert severity="info" sx={{ m: 2, mb: 0 }}>
                                  <Typography variant="caption">
                                    Map filtered to show regional areas with positive ranking progress
                                  </Typography>
                                </Alert>
                              )}
                              {/* Comparison Maps - Only pass baseline and comparison snapshots */}
                              <CardContent sx={{ p: 2, height: '900px' }}>
                                {hasFilteredLocations ? (
                                  <SplitMapComparison
                                  selectedKeyword={keyword.keyword}
                                  rankingHistory={snapshotPairForMap}
                                  businessLocation={selectedBusiness}
                                  onCreateSnapshot={() => {
                                    console.log('Snapshot creation not available in Report view');
                                  }}
                                  loading={snapshotLoading}
                                  />
                                ) : (
                                  <Alert severity="warning">
                                    <Typography variant="body2">
                                      No regional areas with positive progress found. All test locations have been filtered.
                                    </Typography>
                                  </Alert>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              )}

              

              {/* Show message when no snapshots available */}
              {reportData.allKeywords && reportData.allKeywords.length > 0 && rankingHistory.length === 0 && (
                <Card sx={{ mb: 4 }}>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Alert severity="info">
                      <Typography variant="body2" gutterBottom>
                        📊 No ranking snapshots available for comparison yet
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        To compare keyword rankings visually, create snapshots in the Ranking Tracker's Measurement tab. Once created, they will appear here automatically.
                      </Typography>
                    </Alert>
                  </CardContent>
                </Card>
              )}

              <Paper sx={{ mb: 4 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Keyword</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Rank Change</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Difference</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Best Rank</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Worst Rank</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody> 
                      {reportData.allKeywords
                        .sort((a, b) => {
                          const aRank = a.averageRank || 999;
                          const bRank = b.averageRank || 999;
                          return aRank - bRank;
                        })
                        .slice(0, 15)
                        .map((keyword, index) => {
                          // Calculate ranking change display
                          const rankingChange = keyword.rankingChange;
                          const hasRankingData = rankingChange && rankingChange.from && rankingChange.to;
                          const difference = hasRankingData ? rankingChange.from - rankingChange.to : null;
                          const differenceColor = difference > 0 ? 'success' : difference < 0 ? 'error' : 'default';

                          return (
                            <TableRow key={index} hover>
                              <TableCell sx={{ fontWeight: 500 }}>
                                {keyword.keyword}
                              </TableCell>
                              <TableCell align="center">
                                {hasRankingData ? (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: keyword.changeType === 'improved' ? '#4caf50' : keyword.changeType === 'declined' ? '#f44336' : 'inherit',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {rankingChange.from} → {rankingChange.to}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    N/A
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {difference !== null ? (
                                 

                                  <Typography
                                                                sx={{
                                                                  fontWeight: 'bold',
                                                                  color: difference > 0 ? '#4caf50' : difference < 0 ? '#f44336' : 'default'
                                                                }}
                                                              >
                                                                {`${difference > 0 ? '+' : ''}${difference}`}
                                                              </Typography>


                                  
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={`#${keyword.bestRank || 'N/A'}`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={`#${keyword.worstRank || 'N/A'}`}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}

          {/* Regional Keywords Ranking Analysis Table */}
          <RegionalKeywordsRankingAnalysisTable
            locationId={locationId}
            dateRange={dateRange}
          />

          {/* Highlights Section - Categorized by Performance Level */}
          <Card sx={{ mt: 1, mb: 1 }}>
            <CardHeader title="Highlights" />
            <CardContent sx={{ pt: 1 }}>
              <Stack spacing={1}>
                {(() => {
                  // Check for keyword improvements (Best Case indicator)
                  const keywordImproved = reportData.keywordMetrics.improved > 0;

                  // Check for GMB insights improvements (Impressions, Calls, Website Clicks)
                  const gmbInsightsImproved = (
                    (reportData.stats.impressions.current > reportData.stats.impressions.previous && reportData.stats.impressions.previous > 0) ||
                    (reportData.stats.calls.current > reportData.stats.calls.previous && reportData.stats.calls.previous > 0) ||
                    (reportData.stats.websiteClicks.current > reportData.stats.websiteClicks.previous && reportData.stats.websiteClicks.previous > 0)
                  );

                  // Check for activity improvements (Posts, Media, Services, Descriptions)
                  const activityImproved = (
                    (reportData.activities.postsCount.current > reportData.activities.postsCount.previous && reportData.activities.postsCount.previous > 0) ||
                    reportData.activities.mediasCount > 0 ||
                    reportData.serviceMetrics.added.current > 0 ||
                    reportData.serviceMetrics.descriptionAdded.current > 0 ||
                    reportData.serviceMetrics.descriptionUpdated.current > 0
                  );

                  // Define performance categories with configuration
const PERFORMANCE_CONFIG = {
  ALL_IMPROVED: {
    color: "#4caf50",
    conditions: { keyword: true, gmb: true }
  },
  GMB_IMPROVED: {
    color: "#ff9800",
    conditions: { keyword: false, gmb: true }
  },
  ACTIVITY_IMPROVED: {  
    color: "#f34121ff", 
    conditions: { keyword: false, gmb: false, activity: true }
  }
};

// Determine performance category using configuration
const getPerformanceTag = (improvements) => {
  const { keywordImproved, gmbInsightsImproved, activityImproved } = improvements;
  
  let config = null;
  
  if ((keywordImproved && gmbInsightsImproved  ) || keywordImproved   ) {
    config = PERFORMANCE_CONFIG.ALL_IMPROVED;
  } else if (gmbInsightsImproved ) {
    config = PERFORMANCE_CONFIG.GMB_IMPROVED;
  } else if (activityImproved) {
    config = PERFORMANCE_CONFIG.ACTIVITY_IMPROVED;
  }
  
  if (!config) return null;
  
  return (
    <Chip 
      label="PERFORMANCE"
      size="small" 
      sx={{ 
        fontWeight: 'bold', 
        backgroundColor: config.color, 
        color: 'white', 
        mb: 1 
      }} 
    />
  );
};

// Usage
const performanceTag = getPerformanceTag({
  keywordImproved,
  gmbInsightsImproved, 
  activityImproved
});

                  const highlights = [];

                  if (performanceTag) {
                    highlights.push(performanceTag);
                  }

                  // Impressions improvement
                  if (reportData.stats.impressions.current > reportData.stats.impressions.previous && reportData.stats.impressions.previous > 0) {
                    const diff = reportData.stats.impressions.current - reportData.stats.impressions.previous;
                    const percent = ((diff / reportData.stats.impressions.previous) * 100).toFixed(1);
                    highlights.push(
                      <Box key="impressions" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2em' }}>✓</Typography>
                        <Typography>Impressions increased by <strong>{diff.toLocaleString()}</strong> ({percent}%)</Typography>
                      </Box>
                    );
                  }

                  // Calls improvement
                  if (reportData.stats.calls.current > reportData.stats.calls.previous && reportData.stats.calls.previous > 0) {
                    const diff = reportData.stats.calls.current - reportData.stats.calls.previous;
                    const percent = ((diff / reportData.stats.calls.previous) * 100).toFixed(1);
                    highlights.push(
                      <Box key="calls" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2em' }}>✓</Typography>
                        <Typography>Calls increased by <strong>{diff}</strong> ({percent}%)</Typography>
                      </Box>
                    );
                  }

                  // Website Clicks improvement
                  if (reportData.stats.websiteClicks.current > reportData.stats.websiteClicks.previous && reportData.stats.websiteClicks.previous > 0) {
                    const diff = reportData.stats.websiteClicks.current - reportData.stats.websiteClicks.previous;
                    const percent = ((diff / reportData.stats.websiteClicks.previous) * 100).toFixed(1);
                    highlights.push(
                      <Box key="websiteClicks" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2em' }}>✓</Typography>
                        <Typography>Website Clicks increased by <strong>{diff.toLocaleString()}</strong> ({percent}%)</Typography>
                      </Box>
                    );
                  }

                  // Keywords Improved
                  if (reportData.keywordMetrics.improved > 0) {
                    highlights.push(
                      <Box key="keywordsImproved" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2em' }}>✓</Typography>
                        <Typography><strong>{reportData.keywordMetrics.improved}</strong> keyword(s) improved their ranking</Typography>
                      </Box>
                    );
                  }

                  // Regional Keywords Ranking Improvement
                  if (reportData.regionalAnalysis && reportData.regionalAnalysis.keywords && reportData.regionalAnalysis.keywords.length > 0) {
                    // Calculate keywords with improvements and total improvements
                    const keywordsWithImprovements = reportData.regionalAnalysis.keywords.filter(kw => kw.improvements > 0);
                    const totalImprovements = reportData.regionalAnalysis.keywords.reduce((sum, kw) => sum + kw.improvements, 0);

                    if (keywordsWithImprovements.length > 0 && totalImprovements > 0) {
                      highlights.push(
                        <Box key="regionalKeywordsImproved" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2em' }}>✓</Typography>
                          <Typography><strong>{keywordsWithImprovements.length}</strong> keyword(s) improved their regional ranking over <strong>{totalImprovements}</strong> regions</Typography>
                        </Box>
                      );
                    }
                  }

                  // Posts improvement
                  if (reportData.activities.postsCount.current > reportData.activities.postsCount.previous && reportData.activities.postsCount.previous > 0) {
                    const diff = reportData.activities.postsCount.current - reportData.activities.postsCount.previous;
                    const percent = ((diff / reportData.activities.postsCount.previous) * 100).toFixed(1);
                    highlights.push(
                      <Box key="posts" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2em' }}>✓</Typography>
                        <Typography>Posts increased by <strong>{diff}</strong> ({percent}%)</Typography>
                      </Box>
                    );
                  }

                  // Media/Photos added
                  if (reportData.activities.mediasCount > 0) {
                    highlights.push(
                      <Box key="media" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2em' }}>✓</Typography>
                        <Typography><strong>{reportData.activities.mediasCount}</strong> media/photo(s) added</Typography>
                      </Box>
                    );
                  }

                  // Services Added
                  if (reportData.serviceMetrics.added.current > 0) {
                    highlights.push(
                      <Box key="servicesAdded" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2em' }}>✓</Typography>
                        <Typography><strong>{reportData.serviceMetrics.added.current}</strong> service(s) added</Typography>
                      </Box>
                    );
                  }

                  // Descriptions Added
                  if (reportData.serviceMetrics.descriptionAdded.current > 0) {
                    highlights.push(
                      <Box key="descriptionsAdded" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2em' }}>✓</Typography>
                        <Typography><strong>{reportData.serviceMetrics.descriptionAdded.current}</strong> service description(s) added</Typography>
                      </Box>
                    );
                  }

                  // Descriptions Updated
                  if (reportData.serviceMetrics.descriptionUpdated.current > 0) {
                    highlights.push(
                      <Box key="descriptionsUpdated" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2em' }}>✓</Typography>
                        <Typography><strong>{reportData.serviceMetrics.descriptionUpdated.current}</strong> service description(s) updated</Typography>
                      </Box>
                    );
                  }

                  if (highlights.length === 0) {
                    return (
                      <Typography color="textSecondary" sx={{ fontStyle: 'italic' }}>
                        No positive progress to highlight in this period.
                      </Typography>
                    );
                  }

                  return highlights;
                })()}
              </Stack>
            </CardContent>
          </Card>



         { (aiAnalyzing || aiAnalysis) &&

          <Card sx={{ mt: 1, mb: 1 }}>
            <CardHeader
              title=" GMB Profile Performance Assessment"
              action={aiAnalyzing && <CircularProgress size={20} />}
            />
            <CardContent sx={{ pt: 1 }}>
              {aiAnalyzing && !aiAnalysis ? (
                // Loading skeleton
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Skeleton variant="text" height={24} width="40%" />
                  <Skeleton variant="text" height={16} />
                  <Skeleton variant="text" height={16} />
                  <Skeleton variant="text" height={16} width="80%" />
                  <Box sx={{ mt: 2 }}>
                    <Skeleton variant="text" height={24} width="35%" />
                    <Skeleton variant="text" height={16} sx={{ mt: 1 }} />
                    <Skeleton variant="text" height={16} />
                    <Skeleton variant="text" height={16} width="85%" />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Skeleton variant="text" height={24} width="30%" />
                    <Skeleton variant="text" height={16} sx={{ mt: 1 }} />
                    <Skeleton variant="text" height={16} width="90%" />
                  </Box>
                </Box>
              ) : (
                // Actual AI Analysis Content
                <Box sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: 'white',
                }}>
                  {typeof aiAnalysis === 'string' ? (
                    <Typography sx={{ lineHeight: 1.8, color: '#000000ff' }}>
                      {aiAnalysis
                        .replace(/[#*]/g, '')
                        .split('\n')
                        .slice(1, -1)
                        .join('\n')}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#333' }}>
                      {JSON.stringify(aiAnalysis, null, 2)}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>}

          





          {/* Summary */}
          <Card sx={{ mt: 2 }}>
            <CardHeader title=" Report Summary" />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Report Period:</strong> {dateRange.startDate} to {dateRange.endDate} ({duration})
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Business:</strong> {selectedBusiness?.businessName || 'Business'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Generated:</strong> {new Date().toLocaleString()}
                  </Typography>
                </Box>
                
              </Stack>
            </CardContent>
          </Card>


          
          
        </Box>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Select a date range and click "Generate Report" to view your activity report
          </Typography>
        </Paper>
      )}

      <Snackbar
        open={pdfSuccess}
        autoHideDuration={4000}
        onClose={() => setPdfSuccess(false)}
        message="Report PDF downloaded successfully!"
      />
    </Container>
  );
};

export default ReportPage;
